<template>
  <section>
    <b-modal
      id="modal-add-event"
      ref="modal-add-event"
      data-cy="calendar-event-modal"
      :data-view-ready="!loading"
      :modal-class="'has-confirmation-modal'"
      hide-footer
      :ignore-enforce-focus-selector="'.tox,.tox-dialog,.tox-textfield,.tox-selectfield'"
      no-close-on-backdrop>
      <template #modal-header>
        <div class="w-100">
          <h5
            v-if="isEditable"
            class="mb-0 d-inline normal modal-title">
            {{ $t('message.calendar.event.create') }}
          </h5>
          <h5
            v-else
            class="mb-0 d-inline modal-title">
            {{ $t('message.calendar.event.edit') }}
          </h5>
          <coozzy-button
            data-cy="calendar-event-close-button"
            class="float-right"
            design="transparent"
            @click="hideModal()">
            <img
              alt="Placeholder"
              class="close-img"
              src="@/assets/icon_close.svg">
          </coozzy-button>
          <coozzy-button
            v-if="eventDetail !== null && canBeEdited && allowedToDelete"
            data-cy="calendar-event-delete-button"
            class="float-right"
            design="transparent"
            @click="showDeleteConfirmationModal">
            <coozzy-delete-icon />
          </coozzy-button>
          <coozzy-button
            v-if="!isEditable && canBeEdited && isOwner"
            data-cy="calendar-event-edit-button"
            class="float-right"
            design="transparent"
            @click="editEvent(true, null)">
            <coozzy-edit-icon />
          </coozzy-button>
        </div>
      </template>
      <div
        v-if="loading"
        class="d-block text-center text-danger my-2">
        <coozzy-spinner />
      </div>
      <div
        v-else
        class="d-block">
        <div class="row mb-2">
          <div class="col">
            <label>{{ $t('message.navigation.calendar.title') }} </label>
            <coozzy-form-select
              v-if="isEditable"
              v-model="organizer"
              data-cy="calendar-event-owner-select"
              :disabled="eventDetail !== null"
              class="select-entries float-right"
              name="numberEntries">
              <option
                v-for="(employee, index) in availableEmployees"
                :key="index"
                :value="employee">
                {{ employee.firstName }} {{ employee.lastName }}
              </option>
            </coozzy-form-select>
            <p
              v-else
              data-cy="calendar-event-owner-value"
              class="show-data">
              {{ organizer ? getOrganizer : '' }}
            </p>
          </div>
          <div
            v-if="!isAdminModule && source !== 'contact'"
            class="col">
            <label>{{ $t('message.calendar.event.objectReference') }} </label>
            <p v-if="(!isEditable && event.referenceIds.filter(x => x.startsWith('req')).length > 0)">
              <a
                class="lh-1"
                role="button"
                tabindex="0"
                @click="redirectToRequest"
                @click.middle="redirectToRequestMiddleClick">
                {{ objectName }}
              </a>
            </p>
            <coozzy-form-input
              v-else-if="(objectReference && isEditable) || event.referenceIds.filter(x => x.startsWith('req')).length > 0"
              v-model="objectName"
              disabled
              type="text" />
            <p
              v-else-if="event.referenceIds.length === 0 && !isEditable"
              class="show-data">
              {{ $t('message.contact.types.NONE') }}
            </p>
            <a
              v-else-if="(event.referenceIds.length > 0 || objectReference) && !isEditable"
              class="show-data d-block"
              @click="objectUrl"
              @click.middle="objectUrlMiddleClick">
              {{ objectName }}
            </a>
          </div>
        </div>
        <div class="row mb-2">
          <div
            v-if="source !== 'dashbord' || event.private || isEditable"
            class="col-6 col-md-3">
            <coozzy-form-checkbox
              v-if="source === '' || isEditable"
              id="private"
              key="private"
              v-model="event.private"
              data-cy="calendar-event-private-checkbox"
              :disabled="!isEditable || (organizer && organizer.id !== $store.getters['user/getUserId']) || (isEditable && eventDetail && eventDetail.recurringEvent && !editAllRecurringEvents)"
              :initial="event.private"
              class="align-self-center mt-1">
              {{ $t('message.calendar.event.privateEvent') }}
            </coozzy-form-checkbox>
            <p
              v-else-if="event.private"
              class="show-data">
              {{ $t('message.calendar.event.privateEvent') }}
            </p>
          </div>
          <div class="col-6 col-md-3">
            <coozzy-form-checkbox
              v-if="source === '' || isEditable"
              id="allDay"
              key="allDAy"
              v-model="event.allDay"
              data-cy="calendar-event-allday-checkbox"
              :disabled="!isEditable"
              :initial="event.allDay"
              class="align-self-center mt-1">
              {{ $t('message.calendar.event.allDay') }}
            </coozzy-form-checkbox>
            <p
              v-else-if="event.allDay"
              class="show-data">
              {{ $t('message.calendar.event.allDay') }}
            </p>
          </div>
        </div>
        <div class="row mb-2">
          <div class="col-6 col-md-3">
            <label>{{ $t('message.calendar.event.start') }}*</label>
            <coozzy-form-input
              v-if="isEditable"
              id="startDate"
              ref="startDate"
              v-model="event.startDateString"
              data-cy="calendar-event-start-date-input"
              :state="$v.event.startDate.$dirty && $v.event.startDate.$error ? false : null"
              type="date" />
            <p
              v-else
              data-cy="calendar-event-start-date-value"
              class="show-data">
              {{ labelFormatDate(event.startDateString) }}
            </p>
          </div>
          <div
            v-if="source === 'dashbord' && event.allDay"
            class="col" />
          <div
            v-else
            class="col-6 col-md-3">
            <label>{{ $t('message.calendar.event.time') }}</label>
            <coozzy-form-select
              v-if="isEditable"
              id="startTime"
              v-model="event.startTimeString"
              data-cy="calendar-event-start-time-select"
              :disabled="event.allDay">
              <option
                v-for="t in timeList"
                :key="'start' + t"
                :value="t">
                {{ t }}
              </option>
            </coozzy-form-select>
            <p
              v-else
              data-cy="calendar-event-start-time-value"
              class="show-data">
              {{ labelFormatTime(event.startTimeString) }}
            </p>
          </div>
          <div class="col-6 col-md-3">
            <label>{{ $t('message.calendar.event.end') }}*</label>
            <coozzy-form-input
              v-if="isEditable"
              id="endDate"
              ref="endDate"
              v-model="event.endDateString"
              data-cy="calendar-event-end-date-input"
              :state="$v.event.endDate.$dirty && $v.event.endDate.$error ? false : null"
              type="date" />
            <p
              v-else
              data-cy="calendar-event-end-date-value"
              class="show-data">
              {{ labelFormatDate(event.endDateString) }}
            </p>
          </div>
          <div
            v-if="source === 'dashbord' && event.allDay"
            class="col" />
          <div
            v-else
            class="col-6 col-md-3">
            <label>{{ $t('message.calendar.event.time') }}</label>
            <coozzy-form-select
              v-if="isEditable"
              id="endTime"
              v-model="event.endTimeString"
              data-cy="calendar-event-end-time-select"
              :disabled="event.allDay">
              <option
                v-for="t in timeList"
                :key="'end' + t"
                :value="t">
                {{ t }}
              </option>
            </coozzy-form-select>
            <p
              v-else
              data-cy="calendar-event-end-time-value"
              class="show-data">
              {{ labelFormatTime(event.endTimeString) }}
            </p>
          </div>
        </div>
        <!--    Recurring Events Part   -->
        <template
          v-if="isEditable || !eventDetail || (eventDetail && eventDetail.recurringEvent)">
          <div
            class="row mb-2">
            <div
              class="col-12 col-md-6">
              <coozzy-form-checkbox
                id="recurringEvent"
                key="recurringEvent"
                v-model="event.recurringEvent"
                data-cy="calendar-event-recurring-checkbox"
                :disabled="!isEditable || (isEditable && eventDetail && eventDetail.recurringEvent)"
                :initial="event.recurringEvent"
                class="align-self-center">
                {{ $t('message.calendar.event.recurringEvent.checkbox') }}
              </coozzy-form-checkbox>
            </div>
          </div>
          <div
            v-if="event.recurringEvent"
            class="row mb-2">
            <div class="col-12 col-md-12">
              <label for="recurrencePattern">
                {{ $t('message.calendar.event.recurringEvent.recurrencePattern.title') }}
              </label>
            </div>
            <div
              class="col-12 col-md-2">
              <coozzy-form-radio
                v-model="event.frequency"
                data-cy="calendar-event-frequency-daily-radio"
                :default-model="event.frequency"
                :disabled="!isEditable || (isEditable && eventDetail && eventDetail.recurringEvent && !editAllRecurringEvents)"
                :select-value="'EVENT_FREQUENCY_DAILY'"
                :state="$v.event.frequency.$dirty && $v.event.frequency.$error ? false : null"
                name="frequency">
                {{ $t('message.calendar.event.recurringEvent.recurrencePattern.EVENT_FREQUENCY_DAILY') }}
              </coozzy-form-radio>
            </div>
            <div
              class="col-12 col-md-2">
              <coozzy-form-radio
                v-model="event.frequency"
                data-cy="calendar-event-frequency-weekly-radio"
                :default-model="event.frequency"
                :disabled="!isEditable || (isEditable && eventDetail && eventDetail.recurringEvent && !editAllRecurringEvents)"
                :select-value="'EVENT_FREQUENCY_WEEKLY'"
                :state="$v.event.frequency.$dirty && $v.event.frequency.$error ? false : null"
                name="frequency">
                {{ $t('message.calendar.event.recurringEvent.recurrencePattern.EVENT_FREQUENCY_WEEKLY') }}
              </coozzy-form-radio>
            </div>
            <div
              class="col-12 col-md-2">
              <coozzy-form-radio
                v-model="event.frequency"
                data-cy="calendar-event-frequency-monthly-radio"
                :default-model="event.frequency"
                :disabled="!isEditable || (isEditable && eventDetail && eventDetail.recurringEvent && !editAllRecurringEvents)"
                :select-value="'EVENT_FREQUENCY_MONTHLY'"
                :state="$v.event.frequency.$dirty && $v.event.frequency.$error ? false : null"
                name="frequency">
                {{ $t('message.calendar.event.recurringEvent.recurrencePattern.EVENT_FREQUENCY_MONTHLY') }}
              </coozzy-form-radio>
            </div>
            <div
              class="col-2 col-md-2">
              <coozzy-form-radio
                v-model="event.frequency"
                data-cy="calendar-event-frequency-yearly-radio"
                :default-model="event.frequency"
                :disabled="!isEditable || (isEditable && eventDetail && eventDetail.recurringEvent && !editAllRecurringEvents)"
                :select-value="'EVENT_FREQUENCY_YEARLY'"
                :state="$v.event.frequency.$dirty && $v.event.frequency.$error ? false : null"
                name="frequency">
                {{ $t('message.calendar.event.recurringEvent.recurrencePattern.EVENT_FREQUENCY_YEARLY') }}
              </coozzy-form-radio>
            </div>
          </div>
          <div
            v-if="event.frequency !== 'EVENT_FREQUENCY_UNDEFINED' && event.recurringEvent"
            class="row mb-2">
            <div
              class="col-6 col-md-3">
              <label>{{ $t('message.calendar.event.start') }}</label>
              <coozzy-form-input
                v-if="isEditable"
                id="recurrenceStartDate"
                ref="recurrenceStartDate"
                v-model="event.recurrenceStartDateString"
                data-cy="calendar-event-recurrence-start-date-input"
                :state="$v.event.recurrenceStartDate.$dirty && $v.event.recurrenceStartDate.$error ? false : null"
                :disabled="eventDetail && eventDetail.recurringEvent && !editAllRecurringEvents"
                type="date" />
              <p
                v-else
                data-cy="calendar-event-recurrence-start-date-value"
                class="show-data">
                {{ labelFormatDate(event.recurrenceStartDateString) }}
              </p>
            </div>
            <div
              class="col-12 col-md-9">
              <div class="row mt-4">
                <div
                  class="col-6 col-md-3">
                  <coozzy-form-radio
                    v-model="endingRecurrence"
                    data-cy="calendar-event-recurrence-end-by-radio"
                    :default-model="endingRecurrence"
                    :disabled="!isEditable || (isEditable && eventDetail && eventDetail.recurringEvent && !editAllRecurringEvents)"
                    :select-value="'endByDateRecurrence'"
                    name="endingRecurrence">
                    {{ $t('message.calendar.event.recurringEvent.rangeOfRecurrence.endBy') }}
                  </coozzy-form-radio>
                </div>
                <div
                  v-if="endingRecurrence === 'endByDateRecurrence'"
                  class="col-6 col-md-4">
                  <coozzy-form-input
                    v-if="isEditable"
                    id="endBy"
                    ref="endBy"
                    v-model="event.recurrenceEndDateString"
                    data-cy="calendar-event-recurrence-end-by-date-input"
                    :disabled="eventDetail && eventDetail.recurringEvent && !editAllRecurringEvents"
                    :state="$v.event.recurrenceEndDate && $v.event.recurrenceEndDate.$dirty && $v.event.recurrenceEndDate.$error ? false : null"
                    type="date" />
                  <p
                    v-else
                    data-cy="calendar-event-recurrence-end-by-date-value"
                    class="show-data">
                    {{ labelFormatDate(event.recurrenceEndDateString) }}
                  </p>
                </div>
              </div>
              <div class="row">
                <div
                  class="col-4 col-md-3">
                  <coozzy-form-radio
                    v-model="endingRecurrence"
                    data-cy="calendar-event-recurrence-end-after-radio"
                    :default-model="endingRecurrence"
                    :disabled="!isEditable || (isEditable && eventDetail && eventDetail.recurringEvent && !editAllRecurringEvents)"
                    :select-value="'endAfter'"
                    name="endingRecurrence">
                    {{ $t('message.calendar.event.recurringEvent.rangeOfRecurrence.endAfter') }}
                  </coozzy-form-radio>
                </div>
                <div
                  v-if="endingRecurrence === 'endAfter'"
                  class="col-4 d-flex">
                  <coozzy-form-input
                    v-if="isEditable"
                    id="endAfter"
                    ref="endAfter"
                    v-model="event.occurrenceCount"
                    data-cy="calendar-event-recurrence-end-after-input"
                    :state="$v.event?.occurrenceCount && $v.event.occurrenceCount.$dirty && $v.event.occurrenceCount.$error ? false : null"
                    class="mr-2"
                    :disabled="eventDetail && eventDetail.recurringEvent && !editAllRecurringEvents"
                    type="text" />
                  <span
                    v-if="isEditable"
                    class="show-data"> {{ $t('message.calendar.event.recurringEvent.rangeOfRecurrence.occurrences') }}
                  </span>
                  <p
                    v-else
                    data-cy="calendar-event-recurrence-end-after-value"
                    class="show-data">
                    {{ event.occurrenceCount }}
                    {{ $t('message.calendar.event.recurringEvent.rangeOfRecurrence.occurrences') }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </template>
        <div
          v-if="!eventDetail"
          class="row mb-2">
          <div class="col">
            <label>{{ $t('message.settings.template.title') }}</label>
            <coozzy-form-select
              v-model="templateId"
              data-cy="calendar-event-email-template-select">
              <option :value="null" />
              <option
                v-for="template in listTemplates"
                :key="template.id"
                :value="template">
                {{ template.name }}
              </option>
            </coozzy-form-select>
          </div>
        </div>
        <div class="row mb-2">
          <div
            data-cy="calendar-event-title-field"
            class="col">
            <label>{{ $t('message.calendar.event.title') + ' *' }}</label>
            <coozzy-form-input
              id="title"
              v-model="event.title"
              data-cy="calendar-event-title-input"
              :is-read-only="!isEditable"
              :state="$v.event.title.$dirty && $v.event.title.$error ? false : null"
              type="text" />
          </div>
        </div>
        <div class="row mb-2">
          <div
            data-cy="calendar-event-description-field"
            class="col">
            <label>{{ $t('message.calendar.event.description') }}</label>
            <div
              v-if="!isEditable"
              v-dompurify-html="event.description"
              data-cy="calendar-event-description-value"
              class="description-text" />
            <editor
              v-else
              :key="keyRefresh"
              ref="editor-tiny"
              v-model="event.description"
              :api-key="editorApi"
              :init="editorConfig"
              @onInit="updateEditorContentStyle" />
          </div>
        </div>
        <div
          v-if="isEditable"
          class="row mb-2">
          <div class="col">
            <label for="file">{{ $t('message.calendar.event.attachment') }}</label>
            <coozzy-form-file-input
              id="file"
              ref="attachmentUpload"
              data-cy="calendar-event-attachment-input"
              :disabled="uploading"
              :show-name="false"
              accept="text/plain, application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel ,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
              ,application/vnd.ms-powerpoint,  application/vnd.openxmlformats-officedocument.presentationml.presentation, application/pdf,   image/*, video/*, .doc, .docx, .xls, .xlsx, .ppt, .pptx, .pdf, .csv, .emf"
              class=""
              multiple
              @change="addFile" />
            <div
              v-if="filteredMediasList.length > 0"
              class="row">
              <div class="col-12">
                <div class="row">
                  <div class="col-12">
                    <template
                      v-for="media in filteredMediasList">
                      <div
                        v-if="!(['image/jpeg', 'image/png', 'image/gif', 'video/webm', 'video/mp4'].indexOf(media.mimeType) !== -1)"
                        :key="media.id"
                        class="col-12 p-0 square">
                        <div
                          class="file-block">
                          <a
                            :href="media.url"
                            data-v-09485260=""
                            tabindex="0"
                            target="_blank">
                            {{ media.filename }}
                          </a>
                          <coozzy-button
                            class="delete-icon"
                            design="transparent"
                            @click="deleteMedia($event, media.id)">
                            <coozzy-delete-icon />
                          </coozzy-button>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
                <div class="row m-0 img">
                  <template
                    v-for="media in filteredMediasList">
                    <div
                      v-if="['image/jpeg', 'image/png', 'image/gif'].indexOf(media.mimeType) !== -1"
                      :key="media.id"
                      class="media-block p-1 col-2 square">
                      <div
                        :style="{ 'background-image': 'url(' + media.url + ')' }"
                        class="uploaded-file cursor-pointer"
                        style="background-repeat: no-repeat;background-size: cover;background-position: center center;"
                        @click="imageClicked(media)">
                        <coozzy-button
                          class="delete-icon image"
                          design="transparent"
                          @click="deleteMedia($event, media.id)">
                          <coozzy-delete-icon />
                        </coozzy-button>
                      </div>
                    </div>
                  </template>
                </div>
                <div class="row m-0 video">
                  <template
                    v-for="media in filteredMediasList">
                    <div
                      v-if="['video/webm', 'video/mp4'].indexOf(media.mimeType) !== -1"
                      :key="media.id"
                      class="media-block p-1 col-2 square">
                      <coozzy-thumbnail
                        v-if="getMediaUrl(media) !== null"
                        :key="media.id"
                        :video-src="getMediaUrl(media)"
                        :video-url="media.url"
                        class="uploaded-file cursor-pointer"
                        @imageClicked="openVideo(media)" />
                      <coozzy-button
                        class="delete-icon video"
                        design="transparent"
                        @click="deleteMedia($event, media.id)">
                        <coozzy-delete-icon />
                      </coozzy-button>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else-if="filteredMediasList.length > 0"
          data-cy="calendar-event-attachments"
          class="row mb-2">
          <div class="col-12">
            <label v-if="!isEditable">{{ $t('message.calendar.event.attachment') }}</label>
            <div class="row">
              <template
                v-for="media in filteredMediasList">
                <div
                  v-if="!(['image/jpeg', 'image/png', 'image/gif', 'video/webm', 'video/mp4'].indexOf(media.mimeType) !== -1)"
                  :key="media.id"
                  class="col-12 square">
                  <div
                    class="file-block">
                    <a
                      :href="media.url"
                      data-v-09485260=""
                      tabindex="0"
                      target="_blank">
                      {{ media.filename }}
                    </a>
                  </div>
                </div>
              </template>
            </div>
            <div class="row m-0 img">
              <template
                v-for="media in filteredMediasList">
                <div
                  v-if="['image/jpeg', 'image/png', 'image/gif'].indexOf(media.mimeType) !== -1"
                  :key="media.id"
                  class="media-block p-1 col-2 square">
                  <div
                    :style="{ 'background-image': 'url(' + media.url + ')' }"
                    class="uploaded-file cursor-pointer"
                    style="background-repeat: no-repeat;background-size: cover;background-position: center center;"
                    @click="imageClicked(media)" />
                </div>
              </template>
            </div>
            <div class="row m-0 video">
              <template
                v-for="media in filteredMediasList">
                <div
                  v-if="['video/webm', 'video/mp4'].indexOf(media.mimeType) !== -1"
                  :key="media.id"
                  class="media-block p-1 col-2 square">
                  <coozzy-thumbnail
                    v-if="getMediaUrl(media) !== null"
                    :key="media.id"
                    :video-src="getMediaUrl(media)"
                    :video-url="media.url"
                    class="uploaded-file cursor-pointer"
                    @imageClicked="openVideo(media)" />
                </div>
              </template>
            </div>
          </div>
        </div>
        <div class="row mb-2">
          <div class="col-12">
            <label for="notification">
              {{ $t('message.calendar.event.notification') }}
            </label>
          </div>
          <div
            data-cy="calendar-event-reminder-value"
            class="col-6 col-md-3">
            <coozzy-form-select
              v-if="isEditable"
              v-model="reminderInterval"
              data-cy="calendar-event-reminder-select"
              name="reminderInterval">
              <option value="">
                -
              </option>
              <option value="5">
                5 {{ $t('message.generic.minutes') }}
              </option>
              <option value="15">
                15 {{ $t('message.generic.minutes') }}
              </option>
              <option value="30">
                30 {{ $t('message.generic.minutes') }}
              </option>
              <option value="60">
                1 {{ $t('message.generic.hour') }}
              </option>
              <option value="360">
                6 {{ $t('message.generic.hours') }}
              </option>
              <option value="720">
                12 {{ $t('message.generic.hours') }}
              </option>
              <option value="1440">
                1 {{ $t('message.generic.day') }}
              </option>
            </coozzy-form-select>
            <template v-else>
              <template v-if="reminderInterval === '5'">
                5 {{ $t('message.generic.minutes') }}
              </template>
              <template v-if="reminderInterval === '15'">
                15 {{ $t('message.generic.minutes') }}
              </template>
              <template v-if="reminderInterval === '30'">
                30 {{ $t('message.generic.minutes') }}
              </template>
              <template v-else-if="reminderInterval === '60'">
                1 {{ $t('message.generic.hour') }}
              </template>
              <template v-else-if="reminderInterval === '360'">
                6 {{ $t('message.generic.hours') }}
              </template>
              <template v-else-if="reminderInterval === '720'">
                12 {{ $t('message.generic.hours') }}
              </template>
              <template v-else-if="reminderInterval === '1440'">
                1 {{ $t('message.generic.day') }}
              </template>
              <template v-else-if="reminderInterval === ''">
                -
              </template>
            </template>
          </div>
        </div>
        <div class="row mb-2">
          <div
            v-if="isEditable"
            class="col-md-6 align-self-end">
            <div class="row">
              <coozzy-form-radio
                v-model="eventLocation"
                data-cy="calendar-event-onsite-radio"
                :class="eventLocation === 'onSite' ? 'selected' : ''"
                button
                button-variant="outline"
                class="col-6 eventLocation"
                name="eventLocation"
                select-value="onSite">
                {{ $t('message.generic.onSite') }}
              </coozzy-form-radio>
              <coozzy-form-radio
                v-model="eventLocation"
                data-cy="calendar-event-inoffice-radio"
                :class="eventLocation === 'inOffice' ? 'selected' : ''"
                button
                button-variant="outline"
                class="col-6 eventLocation"
                name="eventLocation"
                select-value="inOffice">
                {{ $t('message.generic.inOffice') }}
              </coozzy-form-radio>
            </div>
          </div>
          <div
            data-cy="calendar-event-location-field"
            :class="isEditable ? 'col-md-6' : 'col-12'">
            <label for="location">{{ $t('message.calendar.event.location') }}</label>
            <coozzy-form-input
              id="location"
              v-model="event.location"
              data-cy="calendar-event-location-input"
              :is-read-only="!isEditable"
              type="text" />
          </div>
        </div>
        <div class="row mb-2">
          <div
            v-if="isEditable"
            class="col-12 col-md-6">
            <label>{{ $t('message.calendar.event.searchParticipants') }}</label>
            <contact-search-provider :filters="contactSearchFilters">
              <template #default="{ hits, refine }">
                <vue-autosuggest
                  ref="autosuggest"
                  v-model="searchPartipant"
                  :input-props="{id:'autosuggest__input', 'data-cy': 'calendar-event-attendee-input', class: searchPartipantValidation || searchPartipant === '' ? 'form-control-sm default-border': 'form-control-sm red-border' }"
                  :suggestions="hitsToSuggestions(hits)"
                  @input="onChange(refine,searchPartipant)"
                  @selected="selectHandler"
                  @keyup.enter="checkEnter">
                  <template #default="{ suggestion }">
                    <span class="my-suggestion-item">
                      {{ suggestion.item.name }} {{
                        suggestion.item.email ? '- ' + suggestion.item.email : ''
                      }}</span>
                  </template>
                </vue-autosuggest>
              </template>
            </contact-search-provider>
          </div>
          <!-- <div
            v-if="isEditable && calendarOfUser?.provider === 'PROVIDER_GOOGLE'"
            class="col-6 align-content-end">
            <coozzy-form-checkbox
              id="private"
              key="private"
              v-model="event.notifyParticipants"
              :initial="event.notifyParticipants"
              class="align-self-center mb-1">
              {{ $t('message.calendar.event.sendInvitation') }}
            </coozzy-form-checkbox>
          </div> -->
          <div class="col-12">
            <b-table
              data-cy="calendar-event-attendees-table"
              :fields="fields"
              :items="computedListSelectedAttendees"
              bordered
              stacked="md"
              class="mb-0 mt-3 font-small attendees-table"
              hover>
              <template
                #head(invited)>
                <span>{{ $t('message.calendar.event.invited') }}</span>
              </template>
              <template #head(contactPerson)>
                {{ $t('message.interestedParty.reference.landlord.contactPerson') }}
              </template>
              <template #head(email)>
                {{ $t('message.generic.email') }}
              </template>
              <template
                #head(status)>
                <span>{{ $t('message.generic.status') }}</span>
              </template>
              <template
                #head(editStatus) />
              <template
                #head(action)>
                <span>{{ $t('message.calendar.event.action') }}</span>
              </template>
              <template #cell(invited)="data">
                <div class="d-flex flex-wrap">
                  <span
                    v-if="isEditable && data.item && data.item.name"
                    class="text-break">
                    {{ data.item.name }}
                  </span>
                  <a
                    v-else-if="!isEditable && data.item && data.item.id"
                    class="text-break"
                    role="button"
                    tabindex="0"
                    @click="redirectToDetailPage(data.item.id)"
                    @click.middle="redirectToDetailPageMiddle(data.item.id)">
                    {{ data.item.name }}
                  </a>
                  <span v-else>
                    {{ data.item.name }}
                  </span>
                </div>
              </template>
              <template #cell(contactPerson)="data">
                <coozzy-form-select
                  v-if="data.item.contactPersons && isEditable"
                  id="contactPerson"
                  :ref="'contactPerson_' + data.item.id"
                  v-model="data.item.selectedContactPerson"
                  :class="getContactPersonValidationClass(data.item)"
                  @change="changeNotifiedEmail(data.item)">
                  <option :value="null">
                    -
                  </option>
                  <option
                    v-for="contact of data.item.contactPersons"
                    :key="contact.id"
                    :value="contact.id">
                    {{ contact.name }}
                  </option>
                </coozzy-form-select>
                <span v-else>
                  <span v-if="data.item.contactPersons && !isEditable">
                    {{ getContactName(data.item.contactPersons, data.item.selectedContactPerson) }}
                  </span>
                  <span v-else>
                    -
                  </span>
                </span>
              </template>
              <template #cell(email)="data">
                <coozzy-form-select
                  v-if="data.item && hasWorkEmail(data.item.id) && (data.item.statusEdit || isEditable)"
                  v-model="data.item.email"
                  class="select-entries list-emails-select float-right"
                  name="listEmails">
                  <option
                    v-for="(email, index) in listEmails(data.item.id)"
                    :key="index"
                    :value="email">
                    {{ email }}
                  </option>
                </coozzy-form-select>
                <coozzy-form-input
                  v-else
                  v-model="data.item.email"
                  :disabled="!isEditable"
                  :is-read-only="!isEditable"
                  :allow-click-input-on-table="true"
                  :placeholder="$t('message.generic.email')"
                  :state="getAttendeeEmailValidationState(data.item)" />
              </template>
              <template #cell(status)="data">
                <template v-if="data.item.statusEdit || isEditable">
                  <coozzy-form-select
                    v-model="data.item.status"
                    data-cy="calendar-event-attendee-status-select"
                    class="select-entries float-right"
                    name="numberEntries">
                    <option
                      v-for="(status, index) in attendeesStatus"
                      :key="index"
                      :value="status">
                      {{ $t('message.calendar.event.status.' + status) }}
                    </option>
                  </coozzy-form-select>
                </template>
                <template v-else>
                  <template v-if="data.item.status">
                    {{ $t('message.calendar.event.status.' + data.item.status) }}
                  </template>
                  <template v-else>
                    {{ $t('message.calendar.event.status.ATTENDEE_STATUS_NEEDS_ACTION') }}
                  </template>
                </template>
              </template>
              <template
                v-if="!isEditable && source !== 'email'"
                #cell(editStatus)="data">
                <template
                  v-if="isCurrentUserEmail(data.item.email) || isOwner">
                  <template v-if="data.item && !data.item.statusEdit">
                    <coozzy-button
                      data-cy="calendar-event-attendee-edit-button"
                      class="float-right"
                      design="transparent"
                      @click="editEvent(false, data.item)">
                      <coozzy-edit-icon />
                    </coozzy-button>
                  </template>
                  <template v-else>
                    <coozzy-button
                      data-cy="calendar-event-attendee-save-button"
                      class="float-right"
                      design="transparent"
                      @click="onEditStatusAttendeeClicked(data.item.email !== '' ? data.item.email : data.item.name ? data.item.name : data.item.lastName + '_' + data.item.firstName)">
                      <coozzy-save-icon />
                    </coozzy-button>
                  </template>
                </template>
              </template>
              <template
                #cell(action)="data">
                <coozzy-button
                  v-if="organizer ? data.item.email !== organizer?.email : eventDetail ? data.item.email !== eventDetail.organizer?.email : true"
                  data-cy="calendar-event-attendee-remove-button"
                  design="transparent"
                  @click="removeInvitation(data.item)">
                  <coozzy-delete-icon-img />
                </coozzy-button>
              </template>
            </b-table>
          </div>
        </div>
      </div>
      <div
        v-if="isEditable"
        class="d-block w-100 mt-3">
        <coozzy-button
          data-cy="calendar-event-save-button"
          :disabled="loading"
          class="float-right ml-3"
          design="green"
          @click="checkNewEvent">
          {{ $t('message.generic.form.save') }}
        </coozzy-button>
        <coozzy-button
          data-cy="calendar-event-cancel-button"
          :disabled="loading"
          design="default"
          @click="hideModal()">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
      </div>
      <div
        v-if="userInAttendeesNeedsAction && !isEditable"
        class="d-block w-100 mt-3">
        <coozzy-button
          data-cy="calendar-event-accept-button"
          :disabled="statusUpdating"
          class="float-right ml-3"
          design="prop-green"
          @click="updateStatusButtonsClicked('accept')">
          {{ $t('message.generic.accept') }}
        </coozzy-button>
        <coozzy-button
          data-cy="calendar-event-decline-button"
          :disabled="statusUpdating"
          design="red"
          @click="updateStatusButtonsClicked('decline')">
          {{ $t('message.generic.decline') }}
        </coozzy-button>
      </div>
    </b-modal>

    <b-modal
      :id="'confirmationRecurrence'"
      :ref="'confirmationRecurrence'"
      data-cy="calendar-event-recurrence-edit-modal"
      :title="$t('message.calendar.event.recurringEvent.modal.title')"
      hide-footer
      no-close-on-backdrop>
      <div class="col-12">
        <p>{{ $t('message.calendar.event.recurringEvent.modal.bodyText') }}</p>
      </div>
      <div class="col-12">
        <div
          class="row mb-3">
          <div
            class="col-12">
            <coozzy-form-radio
              v-model="editAllRecurringEvents"
              data-cy="calendar-event-recurrence-edit-single-radio"
              :default-model="editAllRecurringEvents"
              :select-value="false"
              name="type">
              {{ $t('message.calendar.event.recurringEvent.modal.editSingleEvent') }}
            </coozzy-form-radio>
          </div>
          <div
            class="col-12">
            <coozzy-form-radio
              v-model="editAllRecurringEvents"
              data-cy="calendar-event-recurrence-edit-series-radio"
              :default-model="editAllRecurringEvents"
              :select-value="true"
              name="type">
              {{ $t('message.calendar.event.recurringEvent.modal.editAllEvent') }}
            </coozzy-form-radio>
          </div>
        </div>
        <coozzy-button
          data-cy="calendar-event-recurrence-edit-cancel-button"
          class="mb-0 border"
          design="transparent"
          size="small"
          @click="hideConfirmationRecurrenceModal">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          data-cy="calendar-event-recurrence-edit-ok-button"
          class="float-right mb-0"
          design="green"
          size="small"
          @click="acceptEditRecurringEvent">
          {{ $t('message.onBoarding.buildings.objects.inactivePopup.ok') }}
        </coozzy-button>
      </div>
    </b-modal>
    <b-modal
      :id="'delete-confirmation-modal'"
      :ref="'delete-confirmation-modal'"
      data-cy="calendar-event-delete-modal"
      :title="$t('message.calendar.event.deleteConfirmation.title')"
      hide-footer
      no-close-on-backdrop>
      <div class="col">
        <p>{{ $t('message.calendar.event.deleteConfirmation.text') }}</p>
      </div>
      <div class="col">
        <coozzy-button
          data-cy="calendar-event-delete-cancel-button"
          class="mb-0 border"
          design="transparent"
          size="small"
          @click="$bvModal.hide('delete-confirmation-modal')">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          v-if="eventDetail && eventDetail.masterEventId !== ''"
          data-cy="calendar-event-delete-single-button"
          class="float-right mb-0"
          design="green"
          size="small"
          @click="deleteEvent('single')">
          {{ $t('message.calendar.event.recurringEvent.modal.deleteSingleEvent') }}
        </coozzy-button>
        <coozzy-button
          v-if="eventDetail && eventDetail.masterEventId !== ''"
          data-cy="calendar-event-delete-series-button"
          class="float-right mb-0 mr-2"
          design="green"
          size="small"
          @click="deleteEvent('all')">
          {{ $t('message.calendar.event.recurringEvent.modal.deleteAllEvent') }}
        </coozzy-button>
        <coozzy-button
          v-else
          data-cy="calendar-event-delete-confirm-button"
          class="float-right mb-0 mr-2"
          design="green"
          size="small"
          @click="deleteEvent">
          {{ $t('message.generic.delete') }}
        </coozzy-button>
      </div>
    </b-modal>
    <b-modal
      v-if="selectedObjectVideo !== ''"
      id="show-video-modal"
      ref="show-video"
      hide-footer
      hide-header>
      <div class="row mb-2">
        <div class="col">
          <b-embed
            controls
            type="video">
            <source
              :src="selectedObjectVideo"
              type="video/webm">
            <source
              :src="selectedObjectVideo"
              type="video/mp4">
          </b-embed>
        </div>
      </div>
    </b-modal>
    <b-modal
      id="globalConfirmationModal"
      ref="globalConfirmationModal"
      data-cy="calendar-event-discard-modal"
      :title="$t('message.onBoardingTabs.confirmModal.confirmationTitle')"
      hide-footer
      no-close-on-backdrop>
      <p>{{ $t('message.onBoardingTabs.confirmModal.confirmationText') }}</p>
      <div class="col">
        <coozzy-button
          data-cy="calendar-event-discard-cancel-button"
          class="border mb-0"
          design="transparent"
          size="small"
          @click="$bvModal.hide('globalConfirmationModal')">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          data-cy="calendar-event-discard-confirm-button"
          class="float-right mb-0"
          design="green"
          size="small"
          @click="closeCurrentModal">
          {{ $t('message.onBoardingTabs.confirmModal.confirmationButton') }}
        </coozzy-button>
      </div>
    </b-modal>
  </section>
</template>
<script lang="ts">
import CoozzyFormSelect from '@//framework/components/form/select/CoozzyFormSelect.vue'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput.vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import { VueAutosuggest } from 'vue-autosuggest'
import ContactApi from '@/misc/apis/ContactApi'
import ObjectApi from '@/misc/apis/ObjectApi'
import MessageApi from '@/misc/apis/MessageApi'
import MediaApi from '@/misc/apis/MediaApi'
import Vue, { PropType } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import { user } from '@/mixins/user'
import CoozzyDeleteIcon from '@/framework/components/icons/CoozzyDeleteIcon.vue'
import CoozzyEditIcon from '@/framework/components/icons/CoozzyEditIcon.vue'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'
import CoozzyFormCheckbox from '@/framework/components/form/checkbox/CoozzyFormCheckbox.vue'
import { minValue, maxValue, required, email } from 'vuelidate/lib/validators'
import ContactSearchProvider from '@/framework/components/search/ContactSearchProvider.vue'
import { dateUtils } from '@/mixins/dateUtils'
import { calendar } from '@/mixins/calendar'
import { routeChecks } from '@/mixins/routeChecks'
import Editor from '@tinymce/tinymce-vue'
import CoozzyFormFileInput from '@/framework/components/form/fileInput/CoozzyFormFileInput.vue'
import CoozzyThumbnail from '@/framework/components/img/CoozzyThumbnail.vue'
import CoozzySaveIcon from '@/framework/components/icons/CoozzySaveIcon.vue'
import CoozzyFormRadio from '@/framework/components/form/radio/CoozzyFormRadio.vue'
import linkifyStr from 'linkify-string'
import DOMPurify from 'dompurify'
import { media } from '@/mixins/media'
import { responsivity } from '@/mixins/responsivity'
import CoozzyDeleteIconImg from '@/framework/components/icons/CoozzyDeleteIconImg.vue'
import CalendarApi from '@/misc/apis/CalendarApi'
import {
  NylasEventFrequency,
  NylasOrganizer,
  NylasCreateEventRequest, Contactcontactv2Company,
  TypeDate,
  V2Person,
  Templatev2Template
} from '@/types/api'
import { EventObjectReference, SelectedAttendee, SelectOption, UploadedMedia } from '@/types/models'

function moreThanStarting(value, vm) {
  const endDate = new Date(vm.$options.filters.objectToDateInput(value))
  if (vm.event.allDay) {
    return endDate.getTime() / 1000 >= new Date(vm.event.startDateString).getTime() / 1000
  } else {
    endDate.setHours(vm.event.endTime.hours, vm.event.endTime.minutes, 0)
    return new Date(endDate).getTime() / 1000 > new Date(vm.event.startDateString + ' ' + vm.event.startTimeString).getTime() / 1000
  }
}

function moreThanRecurrenceStartDate(value, vm) {
  const recurrenceStartDate = new Date(vm.$options.filters.objectToDateInput(vm.event.recurrenceStartDate))
  const recurrenceEndDate = new Date(vm.$options.filters.objectToDateInput(value))
  return recurrenceEndDate.getTime() > recurrenceStartDate.getTime()
}

function dateOverlaps(value, vm) {
  const startDate = new Date(vm.$options.filters.objectToDateInput(vm.event.startDate))
  const endDate = new Date(vm.$options.filters.objectToDateInput(value))

  let startDateTime, endDateTime
  if (vm.event.allDay) {
    startDateTime = startDate.getTime()
    endDateTime = endDate.getTime()
  } else {
    const startDateWithTime = new Date(startDate)
    startDateWithTime.setHours(vm.event.startTime.hours, vm.event.startTime.minutes, 0)
    const endDateWithTime = new Date(endDate)
    endDateWithTime.setHours(vm.event.endTime.hours, vm.event.endTime.minutes, 0)
    startDateTime = startDateWithTime.getTime()
    endDateTime = endDateWithTime.getTime()
  }

  const duration = endDateTime - startDateTime

  // If event is recurring, validate duration based on frequency
  if (vm.event.recurringEvent && vm.event.frequency) {
    switch (vm.event.frequency) {
      case 'EVENT_FREQUENCY_DAILY':
        // Daily events can't be longer than 1 day
        return duration <= 86400000 // 1 day in milliseconds

      case 'EVENT_FREQUENCY_WEEKLY':
        // Weekly events can't be longer than 1 week
        return duration <= 604800000 // 7 days in milliseconds

      case 'EVENT_FREQUENCY_MONTHLY': {
        // Monthly events can't be longer than 1 month
        const maxMonthlyDate = new Date(startDate)
        maxMonthlyDate.setMonth(maxMonthlyDate.getMonth() + 1)
        if (vm.event.allDay) {
          return endDateTime <= maxMonthlyDate.getTime()
        } else {
          const maxMonthlyDateWithTime = new Date(maxMonthlyDate)
          maxMonthlyDateWithTime.setHours(vm.event.startTime.hours, vm.event.startTime.minutes, 0)
          return endDateTime <= maxMonthlyDateWithTime.getTime()
        }
      }

      case 'EVENT_FREQUENCY_YEARLY': {
        // Yearly events can't be longer than 1 year
        const maxYearlyDate = new Date(startDate)
        maxYearlyDate.setFullYear(maxYearlyDate.getFullYear() + 1)
        if (vm.event.allDay) {
          return endDateTime <= maxYearlyDate.getTime()
        } else {
          const maxYearlyDateWithTime = new Date(maxYearlyDate)
          maxYearlyDateWithTime.setHours(vm.event.startTime.hours, vm.event.startTime.minutes, 0)
          return endDateTime <= maxYearlyDateWithTime.getTime()
        }
      }

      default:
        // For undefined frequency or non-recurring events, default to 1 day check
        return duration <= 86400000
    }
  }

  // Default validation: duration can't be longer than 1 day
  return duration <= 86400000
}

type EventValidation = {
  title: { required: typeof required }
  startDate: { required: typeof required }
  endDate: {
    required?: typeof required
    moreThanStarting?: (value: TypeDate | null) => boolean
    dateOverlaps?: (value: TypeDate | null) => boolean
  }
  recurrenceStartDate?: { required: typeof required }
  recurrenceEndDate?: {
    required: typeof required
    moreThanRecurrenceStartDate: (value: TypeDate | null) => boolean
  }
  occurrenceCount?: {
    required: typeof required
    minValue: ReturnType<typeof minValue>
    maxValue: ReturnType<typeof maxValue>
  }
  frequency?: {
    required: typeof required
    isValidFrequency: (value: NylasEventFrequency | null) => boolean
  }
}

type ValidationsShape = {
  event: EventValidation
  listSelectedAttendees: {
    $each: {
      email: {
        required: typeof required
        email: typeof email
      }
    }
  }
}

interface LocalEvent extends NylasCreateEventRequest {
  recurringEvent?: boolean;
  private?: boolean;
  allDay?: boolean;
  startTimeString?: '';
  endTimeString?: '';
  startDateString?: '';
  endDateString?: '';
  recurrenceStartDateString?: '';
  recurrenceEndDateString?: '';
  organizer?: NylasOrganizer;
  masterEventId?: string;
}

export default Vue.extend({
  name: 'EventCreationModal',
  components: {
    CoozzyDeleteIconImg,
    CoozzyFormRadio,
    CoozzySaveIcon,
    CoozzyThumbnail,
    CoozzyFormFileInput,
    CoozzyFormCheckbox,
    CoozzySpinner,
    CoozzyEditIcon,
    CoozzyDeleteIcon,
    VueAutosuggest,
    CoozzyButton,
    CoozzyFormInput,
    CoozzyFormSelect,
    Editor,
    ContactSearchProvider
  },
  mixins: [user, calendar, routeChecks, dateUtils, media, responsivity],
  props: {
    availableEmployees: {
      type: Array,
      default() {
        return []
      }
    },
    contactsList: {
      type: Array,
      default() {
        return []
      }
    },
    reference: {
      type: Object,
      default: null
    },
    defaultDate: {
      type: Date,
      default() {
        return new Date()
      }
    },
    eventDetail: {
      type: Object as PropType<LocalEvent>,
      default: null
    },
    source: {
      type: String,
      default: ''
    },
    isAllDay: {
      type: Boolean,
      default: false
    },
    objectReference: {
      type: Object,
      default: null
    },
    additionalReferences: {
      type: String,
      default: ''
    },
    canBeEdited: {
      type: Boolean,
      default: true
    },
    userContact: {
      type: Object,
      default: null
    },
    startDateOccurrencesResponse: {
      type: Object,
      default: null
    },
    endDateOccurrencesResponse: {
      type: Object,
      default: null
    },
    availableCalendars: {
      type: Array,
      default() {
        return []
      }
    }
  },
  data() {
    return {
      initialEventDetail: null as LocalEvent | null,
      reminderInterval: '',
      isChanged: false,
      isEditable: this.eventDetail === null,
      object: null as EventObjectReference | null,
      fields: [
        {
          key: 'invited',
          label: this.$t('message.calendar.event.invited'),
          thClass: 'table-header',
          tdClass: 'align-middle'
        },
        {
          key: 'contactPerson',
          label: this.$t('message.interestedParty.reference.landlord.contactPerson'),
          thClass: 'align-middle contact-person-col',
          tdClass: 'align-middle contact-person-col'
        },
        {
          key: 'email',
          label: this.$t('message.generic.email'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        },
        {
          key: 'status',
          label: this.$t('message.generic.status'),
          thClass: 'table-header',
          tdClass: 'align-middle'
        },
        {
          key: 'editStatus',
          label: '',
          thClass: 'table-header max-width-70',
          tdClass: 'align-middle max-width-70'
        }
      ],
      templateId: null as Templatev2Template | null,
      event: {
        title: '',
        startDate: null,
        startDateString: '',
        endDate: null,
        endDateString: '',
        startTime: null,
        startTimeString: '',
        endTime: null,
        endTimeString: '',
        recurrenceStartDate: null,
        recurrenceStartDateString: '',
        recurrenceEndDate: null,
        recurrenceEndDateString: null,
        description: '',
        location: '',
        private: false,
        allDay: this.isAllDay,
        referenceIds: [],
        userId: '',
        mediaIds: [],
        frequency: 'EVENT_FREQUENCY_UNDEFINED',
        recurringEvent: false,
        occurrenceCount: 0,
        attendees: [],
        calendarId: '',
        ownerId: '',
        reminders: [],
        visibility: 'EVENT_VISIBILITY_UNDEFINED',
        notifyParticipants: true
      } as LocalEvent,
      endingRecurrence: 'endByDateRecurrence',
      listReferences: [] as (V2Person | Contactcontactv2Company)[],
      searchPartipant: '',
      searchPartipantValidation: true,
      selectedEmployees: [] as SelectOption[],
      loading: false,
      timeList: [] as string[],
      organizer: null as SelectOption | null,
      attendeesStatus: ['ATTENDEE_STATUS_NEEDS_ACTION', 'ATTENDEE_STATUS_ACCEPTED', 'ATTENDEE_STATUS_DECLINED'],
      loaded: false,
      uploading: false,
      mediasList: [] as UploadedMedia[],
      attachment: [] as File[],
      selectedObjectVideo: '' as string,
      galleryImages: [] as string[],
      keyRefresh: 0,
      eventLocation: '',
      listSelectedAttendees: [] as SelectedAttendee[],
      listContactAttendees: [] as (V2Person | Contactcontactv2Company)[],
      showCompanyAttendeesValidation: false,
      invalidCompanyAttendeeKeys: new Set<string>(),
      editAllRecurringEvents: false,
      attendeeToEdit: null as SelectedAttendee | null,
      newStatusButton: '',
      statusUpdating: false,
      modalHiddenHandler: null as ((bvEvent: unknown, modalId: string) => void) | null,
      modalShowHandler: null as ((bvEvent: unknown, modalId: string) => void) | null
    }
  },
  computed: {
    ...mapState('message', ['templates']),
    contactSearchFilters() {
      return { isOwner: false, active: true }
    },
    calendarOfUser() {
      return this.availableCalendars.find(cal => cal.userId === this.userId)
    },
    organizerCalendar() {
      const organizerUserId = this.organizer?.id || this.eventDetail?.userId
      return this.availableCalendars.find(cal => cal.userId === organizerUserId)
    },
    listTemplates(): Templatev2Template[] {
      return this.templates.slice().sort((a, b) => {
        if (a.name > b.name) {
          return 1
        }
        if (b.name > a.name) {
          return -1
        }
        return 0
      })
    },
    fontSetting() {
      return this.$store.getters['user/getFontSettings']
    },
    contentStyle() {
      const defaultFontFamily = 'Lato, sans-serif'
      const fontFamily = this.fontSetting && this.fontSetting.family !== '' ? `${this.fontSetting.family}` : defaultFontFamily
      const fontSize = this.fontSetting && this.fontSetting.size !== '' ? this.fontSetting.size + 'pt' : '10pt'
      return `body { color: #495057 !important; font-family: ${fontFamily} !important; font-size: ${fontSize}; p {margin: 0}}`
    },
    fontsizeFormats() {
      let sizeOptions = []
      for (let i = 4; i <= 36; i++) {
        sizeOptions.push(`${i}pt`)
      }
      return sizeOptions.join(' ')
    },
    fontFormats() {
      const defaultFont = 'Lato=Lato, sans-serif;'
      const userFont = this.fontSetting && this.fontSetting.family !== '' ? `${this.fontSetting.family}=${this.fontSetting.family}, sans-serif;` : defaultFont
      return `${userFont}`
    },
    allowedToDelete() {
      const calendarEmails = [this.userEmail, this.calendarOfUser?.associatedEmail, this.calendarOfUser?.nylasCalendarId]
        .map(this.normalizeEmail)
        .filter(Boolean)
      const organizerEmail = this.normalizeEmail(this.eventDetail?.organizer?.email)
      const isOrganizer = organizerEmail ? calendarEmails.includes(organizerEmail) : false
      const isAttendeeOnUserCalendar = this.eventDetail?.attendees?.some(attendee => {
        const attendeeEmail = this.normalizeEmail(attendee?.email)
        return attendeeEmail && calendarEmails.includes(attendeeEmail)
      }) || false

      return (isOrganizer || isAttendeeOnUserCalendar) && this.eventDetail?.userId === this.userId
    },
    isOwner() {
      const organizerEmail = this.normalizeEmail(this.eventDetail?.organizer?.email)
      return this.eventDetail &&
        this.$store.getters['user/getUserId'] === this.eventDetail?.userId &&
        this.calendarOfUser &&
        (this.normalizeEmail(this.calendarOfUser.associatedEmail) === organizerEmail ||
          this.normalizeEmail(this.calendarOfUser.nylasCalendarId) === organizerEmail)
    },
    computedListSelectedAttendees() {
      // make the userContact first in the list then sort alphabetically by name
      return this.listSelectedAttendees.slice().sort((a, b) => {
        const email = (this.organizer ? this.organizer.email : this.eventDetail?.organizer?.email) || ''
        if (a.email === email) {
          return -1
        }
        if (b.email === email) {
          return 1
        }
        if (a.name < b.name) return -1
        if (a.name > b.name) return 1
        return 0
      })
    },
    userInAttendeesNeedsAction() {
      return this.eventDetail &&
        this.eventDetail.attendees?.length > 0 &&
        this.eventDetail.attendees.find(x => this.isCurrentUserEmail(x.email) && x.status === 'ATTENDEE_STATUS_NEEDS_ACTION') !== undefined
    },
    userInAttendeesAcceptDecline() {
      return this.eventDetail &&
        this.eventDetail.attendees?.length > 0 &&
        this.eventDetail.attendees.find(x => this.isCurrentUserEmail(x.email) && ['ATTENDEE_STATUS_ACCEPTED', 'ATTENDEE_STATUS_DECLINED'].includes(x.status)) !== undefined
    },
    filteredMediasList() {
      const array = this.mediasList
      array.sort((val1, val2) => {
        return parseInt(val2.uploadedAt) - parseInt(val1.uploadedAt)
      })
      return array
    },
    objectName() {
      if (this.objectReference) {
        return this.objectReference.name
      } else if (this.object) {
        return this.object.name
      }
      return ''
    },
    checkSelectedEmloyees() {
      return this.selectedEmployees.filter(x => x.email !== '').length > 0
    },
    getOrganizer() {
      if (this.organizer) {
        return this.organizer.firstName ? this.organizer.firstName + ' ' + this.organizer.lastName : this.organizer.profile ? this.organizer.profile.firstName + ' ' + this.organizer.profile.lastName : ''
      }
      return ''
    },
    editorApi() {
      return 'gpl'
    },
    editorConfig() {
      return {
        toolbar: 'undo redo | bold italic underline forecolor | fontsize numlist bullist link table | fullscreen print',
        plugins: 'fullscreen lists code image table link autolink',
        height: '150',
        menubar: '',
        ui_container: '#modal-add-event',
        language: this.currentLanguage,
        elementpath: false,
        invalid_styles: {
          '*': 'font-family'
        },
        automatic_uploads: true,
        license_key: 'gpl',
        content_style: this.contentStyle,
        font_formats: this.fontFormats,
        branding: false,
        remove_trailing_brs: false,
        browser_spellcheck: true,
        table_toolbar: '',
        font_size_formats: this.fontsizeFormats,
        convert_urls: false,
        link_title: false,
        link_default_protocol: 'https',
        body_id: 'content',
        text_patterns: [],
        setup: (editor) => {
          editor.on('init', () => {
            const body = editor.getBody()
            body.style.fontFamily = 'Lato, sans-serif'
            editor.focus()

            // Add 'eventTable' class to any existing tables
            setTimeout(() => {
              const tables = body.querySelectorAll('table')
              tables.forEach(table => {
                if (!table.classList.contains('eventTable')) {
                  table.classList.add('eventTable')
                }
              })
            }, 100)
          })

          // Same paste behaviour as SendMail modal: preserve line breaks using paragraphs
          let isPasting = false

          editor.on('paste_preprocess', (plugin, args) => {
            isPasting = true

            const tempDiv = document.createElement('div')
            tempDiv.innerHTML = args.content

            const paragraphs = tempDiv.querySelectorAll('p')
            paragraphs.forEach(p => {
              const textContent = p.textContent.trim()
              const innerHTML = p.innerHTML.trim()

              if (textContent === '' || innerHTML === '' || innerHTML === '<br>' || innerHTML === '<br/>') {
                p.setAttribute('data-empty-para', 'true')
                p.innerHTML = '&nbsp;'
              }
            })

            args.content = tempDiv.innerHTML
          })

          editor.on('paste_postprocess', (plugin, args) => {
            const body = editor.getBody()

            const emptyParagraphs = body.querySelectorAll('p[data-empty-para="true"]')
            emptyParagraphs.forEach(p => {
              p.removeAttribute('data-empty-para')
              p.innerHTML = '<br>'
            })

            const allParagraphs = body.querySelectorAll('p')
            allParagraphs.forEach(p => {
              const textContent = p.textContent.trim()
              const innerHTML = p.innerHTML.trim()

              if ((textContent === '' || innerHTML === '' || innerHTML === '&nbsp;') && !p.hasAttribute('data-empty-para')) {
                p.innerHTML = '<br>'
              }
            })

            isPasting = false
            this.postProcessPaste(plugin, args)
          })

          editor.on('paste', (e) => {
            const clipboardData = e.clipboardData || (window as Window & { clipboardData?: DataTransfer }).clipboardData

            if (!clipboardData) return

            const htmlData = clipboardData.getData('text/html')
            const textData = clipboardData.getData('text/plain')

            // Single-line check BEFORE html check: clipboard often includes both text/html and
            // text/plain even for plain text. Without this, TinyMCE wraps pasted text in <div> blocks.
            if (textData) {
              const lines = textData.split(/\r?\n/)

              // Detect single-line text: only one non-empty line at the start,
              // followed by trailing empty lines (clipboard artifact from other apps e.g. "hello\n")
              const isSingleLine = lines.length >= 1 && lines[0].trim() !== '' &&
                lines.slice(1).every(line => line.trim() === '')

              if (isSingleLine) {
                // Single line: insert inline so it stays on same line
                e.preventDefault()
                editor.insertContent(editor.dom.encode(lines[0]))
                return
              }
            }

            // Multi-line with HTML formatting: let TinyMCE handle it to preserve formatting
            if (htmlData && htmlData.trim() !== '') {
              return
            }

            // Plain text multi-line: preserve line breaks with paragraphs
            if (textData) {
              e.preventDefault()
              const lines = textData.split(/\r?\n/)
              const paragraphs = lines.map(line => {
                if (line.trim() === '') {
                  return '<p><br></p>'
                }
                return '<p>' + line + '</p>'
              })
              editor.insertContent(paragraphs.join(''))
            }
          })

          editor.on('PastePostProcess', () => {
            const body = editor.getBody()

            const paragraphs = body.querySelectorAll('p')
            paragraphs.forEach(p => {
              const textContent = p.textContent.trim()
              const innerHTML = p.innerHTML.trim()

              if (textContent === '' || innerHTML === '' || innerHTML === '&nbsp;' || innerHTML === '<br>' || innerHTML === '<br/>') {
                p.innerHTML = '<br>'
              }
            })
          })

          // Add 'eventTable' class to tables when inserted
          const addEventTableClass = () => {
            const body = editor.getBody()
            // Editor may already be destroyed when SetContent/NodeChange timeouts fire after modal close
            if (!body) return
            const tables = body.querySelectorAll('table')
            tables.forEach(table => {
              if (!table.classList.contains('eventTable')) {
                table.classList.add('eventTable')
              }
            })
          }

          editor.on('SetContent', () => {
            setTimeout(addEventTableClass, 0)
          })

          // Handle tables inserted via table plugin UI or paste
          editor.on('NodeChange', () => {
            setTimeout(addEventTableClass, 0)
          })
        }
      }
    }
  },
  watch: {
    'event.recurringEvent': function (val) {
      if (!val) {
        this.endingRecurrence = 'endByDateRecurrence'
        this.event.recurrenceEndDateString = ''
        this.event.occurrenceCount = 0
        this.event.frequency = 'EVENT_FREQUENCY_UNDEFINED'
        this.event.recurrenceStartDateString = ''
        this.event.recurrenceStartDate = null
        this.event.recurrenceEndDate = null
      }
    },
    'event.frequency': function (val) {
      if (val !== 'EVENT_FREQUENCY_UNDEFINED' && this.event.recurrenceStartDate === null) {
        this.event.recurrenceStartDateString = this.event.startDateString
        this.event.recurrenceStartDate = this.toObject(this.event.startDateString)
      }
    },
    'event.recurrenceStartDateString': function (val) {
      if (this.isEditable && val !== '') {
        this.$refs.startDate = val
        this.event.startDateString = val
        this.event.startDate = this.toObject(val)
      }
    },
    'event.recurrenceEndDateString': function (val) {
      if (this.isEditable && val !== '') {
        this.event.recurrenceEndDate = this.toObject(val)
      }
    },
    reminderInterval: function (val) {
      if (val !== '') {
        if (this.event.reminders.length > 0) {
          this.event.reminders[0].method = 'REMINDER_METHOD_POPUP'
          this.event.reminders[0].reminderMinutes = val
        } else {
          this.event.reminders.push({
            method: 'REMINDER_METHOD_POPUP',
            reminderMinutes: val
          })
        }
      } else {
        this.event.reminders = []
      }
    },
    templateId: function (val) {
      if (val !== null) {
        this.renderTemplate()
      }
    },
    isEditable: function (newVal) {
      if (newVal) {
        this.fields.push({
          key: 'Action',
          label: '',
          tdClass: 'align-middle'
        })
        this.event.description = this.eventDetail.description.replace(/\n/g, '')
      }
      this.loaded = true
    },
    organizer: function (newVal, oldVal) {
      if (newVal) {
        // if (newVal.id !== this.$store.getters['user/getUserId']) {
        //   this.event.private = false
        // }
        const blockedEmails = [
          newVal.email,
          this.organizerCalendar?.associatedEmail,
          this.organizerCalendar?.nylasCalendarId
        ].filter(email => email && email !== '')
        const blockedIds = [newVal.id].filter(id => id && id !== '')
        this.listSelectedAttendees = this.listSelectedAttendees.filter(element => !blockedEmails.includes(element.email) && !blockedIds.includes(element.id || element.objectID))
        this.selectedEmployees = this.selectedEmployees.filter(element => !blockedEmails.includes(element.email) && !blockedIds.includes(element.id || element.objectID))
        if (this.isEditable) {
          this.$store.dispatch('user/setChangedModal', true).then().catch(e => console.log(e))
        }
      }
    },
    event: {
      deep: true,
      handler: function () {
        if (this.isEditable && this.loaded && this.canBeEdited) {
          this.isChanged = true
          this.$store.dispatch('user/setChangedModal', true).then().catch(e => console.log(e))
        }
      }
    },
    'event.startTimeString': function (val) {
      const index = this.timeList.indexOf(val)
      const indexOfEndTime = this.timeList.indexOf(this.event.endTimeString)
      this.event.startTime = { hours: parseInt(val.split(':')[0], 10), minutes: parseInt(val.split(':')[1], 10) }
      if (!this.eventDetail || index >= indexOfEndTime) {
        this.event.endTimeString = this.timeList[index + 4 - (index >= this.timeList.length - 4 ? this.timeList.length : 0)]
        this.event.endTime = {
          hours: parseInt(this.event.endTimeString.split(':')[0], 10),
          minutes: parseInt(this.event.endTimeString.split(':')[1], 10)
        }
        if (index >= this.timeList.length - 4) {
          let endDate = new Date(this.event.startDateString)
          endDate.setDate(endDate.getDate() + 1)
          this.event.endDateString = endDate.getFullYear() + '-' + ('0' + (endDate.getMonth() + 1)).slice(-2) + '-' + ('0' + endDate.getDate()).slice(-2)
          this.event.endDate = this.toObject(this.event.endDateString)
        }
      }
      if (this.loaded) {
        this.changeAttendeesStatus()
      }
    },
    'event.startDateString': function (val) {
      if (val !== '') {
        this.$refs.startDate = val
        this.event.startDate = this.toObject(val)
      }
      if (this.isEditable) {
        this.$refs.endDate = val
        this.event.endDateString = val
        this.event.endDate = this.toObject(val)
        if (this.event.recurringEvent) {
          this.$refs.recurrenceStartDate = val
          this.event.recurrenceStartDateString = val
          this.event.recurrenceStartDate = this.toObject(val)
        }
      }
      if (this.loaded) {
        this.changeAttendeesStatus()
      }
    },
    'event.endDateString': function (val) {
      if (this.loaded) {
        this.event.endDate = this.toObject(val)
        this.changeAttendeesStatus()
      }
    },
    'event.endTimeString': function (val) {
      if (this.loaded) {
        this.event.endTime = {
          hours: parseInt(val.split(':')[0], 10),
          minutes: parseInt(val.split(':')[1], 10)
        }
        this.changeAttendeesStatus()
      }
    },
    endingRecurrence: function () {
      if (this.loaded) {
        this.event.recurrenceEndDateString = ''
        this.event.occurrenceCount = 0
      }
    },
    eventLocation: function (val) {
      this.getLocation(val)
    }
  },
  mounted: function () {
    this.getListTimes()
    this.loadTemplateList(this.accountId)

    if (this.eventDetail) {
      this.initialEventDetail = this.eventDetail
      if (this.eventDetail.mediaIds.length > 0) {
        MediaApi.getListMedia(this.eventDetail.mediaIds)
          .then(response => {
            for (const media of response.media) {
              this.mediasList.push(media)
            }
          })
          .catch(() => {
            Vue.toasted.show(this.$t('message.loadingErrors.media'), { type: 'error' })
          })
      }
      if (this.eventDetail.attendees.length > 0) {
        const attendees = this.eventDetail.attendees.map(x => x.contactId)
        this.loadContacts(attendees)
      }
    }
    this.modalHiddenHandler = (bvEvent, modalId) => {
      if (modalId === 'modal-add-event') {
        this.$emit('hide-modal')
      }
      if (modalId === 'modal-add-event' && this.eventDetail === null) {
        this.event = {
          title: '',
          startDate: '',
          endDate: '',
          startTime: '',
          endTime: '',
          description: '',
          location: '',
          private: false,
          allDay: false,
          referenceIds: [],
          userId: '',
          reminders: [],
          frequency: 'EVENT_FREQUENCY_UNDEFINED',
          recurringEvent: false,
          recurrenceEndDate: null,
          occurrenceCount: 0
        }
        this.statusUpdating = false
        this.endingRecurrence = 'endByDateRecurrence'
        this.selectedEmployees = []
        this.listSelectedAttendees = []
        this.isChanged = false
        this.$nextTick(function () {
          this.$store.dispatch('user/setChangedModal', false).then().catch(e => console.log(e))
        })
      }
    }
    this.modalShowHandler = (bvEvent, modalId) => {
      if (modalId === 'modal-add-event' && this.eventDetail === null) {
        this.$nextTick(function () {
          this.keyRefresh += 1
        })
      }

      this.statusUpdating = false
      if (this.isEditable && this.additionalReferences !== '') {
        this.eventLocation = 'onSite'
        this.getLocation('onSite')
      }
      if (this.isEditable) {
        this.fields.splice(4, 1)
      }
    }
    this.$root.$on('bv::modal::hidden', this.modalHiddenHandler)
    this.$root.$on('bv::modal::show', this.modalShowHandler)
    if (this.eventDetail === null) {
      if (this.contactsList.length > 0) {
        this.selectedEmployees = this.selectedEmployees.concat(this.contactsList)
      } else if (this.reference !== null) {
        this.selectedEmployees.push(this.availableEmployees.find(obj => obj.email === this.reference.email))
      }
      this.$nextTick(function () {
        this.selectedEmployees.forEach(element => {
          this.listSelectedAttendees.push({
            name: element.name ? element.name : element.lastName + ' ' + element.firstName,
            email: element.email,
            id: element.id || element.objectID,
            status: element.status || 'ATTENDEE_STATUS_NEEDS_ACTION',
            contactPersons: this.filterActiveContactPersons(element),
            selectedContactPerson: null,
            statusEdit: false
          })
        })
      })
    }
    if (this.availableEmployees && this.availableEmployees.length > 0) {
      // if (this.eventDetail && this.eventDetail.organizer) {
      //   this.organizer = this.availableEmployees.find(employee => employee.email === this.eventDetail.organizer.email) || null
      // } else
      if (this.eventDetail && this.eventDetail.userId) {
        this.organizer = this.availableEmployees.find(employee => employee.id === this.eventDetail.userId) || null
      } else {
        this.organizer = this.availableEmployees.find(employee => employee.email && employee.email.toLowerCase() === this.$store.getters['user/getCurrentUser'].email.toLowerCase()) || null
      }
    }
    if (this.eventDetail !== null) {
      const contactIds = []
      const userEmails = []
      this.reminderInterval = this.eventDetail.reminders?.length > 0 ? this.eventDetail.reminders[0].reminderMinutes : ''
      this.eventDetail.attendees.forEach(element => {
        // check if attendees has contactId
        if (element.contactId !== '' && element.contactId !== null && element.contactId !== undefined) {
          contactIds.push(element.contactId)
        } else {
          let org = null
          this.availableEmployees ? org = this.availableEmployees.find(obj => obj.email === element.email) : org = null
          if (org && org.email !== '') {
            userEmails.push(org.email)
            // org.status = element.status
            // this.selectedEmployees.push(org)
          } else {
            this.selectedEmployees.push({
              name: element.displayName,
              email: element.email,
              status: element.status,
              id: element.id
            })
          }
        }
      })
      this.$nextTick(function () {
        const promises = []
        if (contactIds.length > 0) {
          promises.push(ContactApi.contactResolve(contactIds))
        }
        if (userEmails.length > 0) {
          promises.push(ContactApi.resolveByMail(this.accountId, userEmails))
        }
        Promise.all(promises).then(globalResponse => {
          let list = []
          globalResponse.forEach(response => {
            // keep only contacts that has an id on contactIds or has email on userEmails
            list = list.concat(response.persons).concat(response.companies)
              .filter(contact => contactIds.includes(contact.id) || (('contactPersons') in contact && contact.contactPersons.some(cp => contactIds.includes(cp.id))) || userEmails.includes(contact.email))
          })
          // remove duplicated contacts
          list = [...new Map(list.map(item => [item.id, item])).values()]
          this.selectedEmployees = this.selectedEmployees.concat(list)
          this.$nextTick(function () {
            this.eventDetail.attendees.forEach(attendee => {
              const contact = this.selectedEmployees.find(x => x.id === attendee.contactId || (('contactPersons') in x && x.contactPersons.some(cp => cp.id === attendee.contactId)) || (attendee.contactId === '' && x.email === attendee.email))
              if (contact) {
                if (('contactPersons') in contact && contact.contactPersons.some(cp => cp.id === attendee.contactId)) {
                  const contactPerson = contact.contactPersons.find(x => x.id === attendee.contactId)
                  this.listSelectedAttendees.push({
                    name: contact.name ? contact.name : (contact.lastName && contact.firstName ? contact.lastName + ' ' + contact.firstName : contact.email),
                    email: contactPerson.email !== '' ? contactPerson.email : '',
                    id: contact.id || contact.objectID || '',
                    status: attendee ? attendee.status : 'ATTENDEE_STATUS_NEEDS_ACTION',
                    contactPersons: this.filterActiveContactPersons(contact),
                    selectedContactPerson: contactPerson ? contactPerson.id : null,
                    statusEdit: false
                  })
                } else {
                  this.listSelectedAttendees.push({
                    name: contact.name ? contact.name : (contact.lastName && contact.firstName ? contact.lastName + ' ' + contact.firstName : contact.email),
                    email: contact.email,
                    id: contact.id || contact.objectID || '',
                    status: attendee ? attendee.status : 'ATTENDEE_STATUS_NEEDS_ACTION',
                    contactPersons: this.filterActiveContactPersons(contact),
                    selectedContactPerson: null,
                    statusEdit: false
                  })
                }
              }
            })
          })
        })
      })
      const isWholeDay = this.eventDetail.startTime === null || (this.eventDetail.startTime.hours === 0 && this.eventDetail.startTime.minutes === 0 && this.eventDetail.endTime.hours === 23 && this.eventDetail.endTime.minutes === 59)
      this.event = {
        id: this.eventDetail.id,
        title: this.decodeHtmlEntitiesIfNeeded(this.eventDetail.title),
        startDateString: this.formatDateForCalendar(this.eventDetail.startDate),
        startDate: this.eventDetail.startDate,
        recurrenceStartDateString: this.formatDateForCalendar(this.eventDetail.recurrenceStartDate),
        recurrenceStartDate: this.eventDetail.recurrenceStartDate,
        endDateString: this.formatDateForCalendar(this.eventDetail.endDate),
        endDate: this.eventDetail.endDate,
        recurrenceEndDateString: this.formatDateForCalendar(this.eventDetail.recurrenceEndDate),
        recurrenceEndDate: this.eventDetail.recurrenceEndDate,
        startTimeString: this.formatTimeForCalendar(this.eventDetail.startTime),
        startTime: this.eventDetail.startTime,
        endTimeString: this.formatTimeForCalendar(this.eventDetail.endTime),
        endTime: this.eventDetail.endTime,
        description: this.eventDetail.description,
        location: this.decodeHtmlEntitiesIfNeeded(this.eventDetail.location || ''),
        private: this.eventDetail.private,
        allDay: isWholeDay,
        url: this.eventDetail.url,
        referenceIds: this.eventDetail.referenceIds,
        organizer: this.organizer,
        userId: this.eventDetail.userId,
        reminders: this.eventDetail.reminders,
        frequency: this.eventDetail.frequency,
        recurringEvent: this.eventDetail.recurringEvent,
        occurrenceCount: this.eventDetail.occurrenceCount,
        notifyParticipants: this.eventDetail.notifyParticipants || true
      }

      if (this.event.occurrenceCount !== 0) {
        this.endingRecurrence = 'endAfter'
      } else if (this.event.recurrenceEndDate) {
        this.endingRecurrence = 'endByDateRecurrence'
      }
      if (this.event.startTimeString === '00:00' && this.event.endTimeString === '00:00') {
        this.event.allDay = true
      }
      this.$nextTick(function () {
        this.event.endTimeString = this.formatTimeForCalendar(this.eventDetail.endTime)
        this.$store.dispatch('user/setChangedModal', false).then().catch(e => console.log(e))
      })
    } else if (this.defaultDate) {
      const hours = ('0' + (this.defaultDate.getHours())).slice(-2)
      this.event.startDateString = this.defaultDate.getFullYear() + '-' + ('0' + (this.defaultDate.getMonth() + 1)).slice(-2) + '-' + ('0' + this.defaultDate.getDate()).slice(-2)
      this.event.startTimeString = hours + ':00'
      this.event.startDate = this.toObject(this.event.startDateString)
      this.event.startTime = { hours: parseInt(hours), minutes: 0 }
      this.$nextTick(function () {
        this.$store.dispatch('user/setChangedModal', false).then().catch(e => console.log(e))
      })
      this.fields.push({
        key: 'Action',
        label: '',
        tdClass: 'align-middle'
      })
    }
    if (this.event.referenceIds.length > 0) {
      const objId = this.event.referenceIds.find(x => x.startsWith('obj'))
      if (objId !== undefined && objId !== '') {
        ObjectApi.getObject(objId)
          .then(response => {
            this.object = response.object
          })
          .catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.loadingErrors.object'), { type: 'error' })
          })
      }
      const ids = this.event.referenceIds.filter(x => x && x.startsWith('contact'))
      if (ids.length > 0) {
        ContactApi.contactResolve(ids)
        .then(response => {
          this.listReferences = ((response.persons as (V2Person | Contactcontactv2Company)[]).concat(response.companies))
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
        })
      }
    }
    this.$nextTick(function () {
      if (this.isEditable) {
        this.loaded = true
      }
    })
  },
  beforeDestroy() {
    if (this.modalHiddenHandler) {
      this.$root.$off('bv::modal::hidden', this.modalHiddenHandler)
    }
    if (this.modalShowHandler) {
      this.$root.$off('bv::modal::show', this.modalShowHandler)
    }
  },
  methods: {
    ...mapActions('message', ['loadTemplateList']),
    normalizeEmail(email) {
      return typeof email === 'string' ? email.toLowerCase() : ''
    },
    isCurrentUserEmail(email) {
      const normalizedEmail = this.normalizeEmail(email)
      if (!normalizedEmail) {
        return false
      }
      const currentUserEmails = [this.userEmail, this.calendarOfUser?.associatedEmail, this.calendarOfUser?.nylasCalendarId]
        .map(this.normalizeEmail)
        .filter(Boolean)
      return currentUserEmails.includes(normalizedEmail)
    },
    filterActiveContactPersons(contact) {
      if (!('contactPersons' in contact) || !contact.contactPersons?.length) {
        return null
      }
      const activeContactPersons = contact.contactPersons.filter(cp => cp.active !== false)
      return activeContactPersons.length > 0 ? activeContactPersons : null
    },
    getOrganizerEmails() {
      return [
        this.organizer?.email,
        this.organizerCalendar?.associatedEmail,
        this.organizerCalendar?.nylasCalendarId
      ].map(this.normalizeEmail).filter(email => email && email !== '')
    },
    isOrganizerEmail(email) {
      const normalizedEmail = this.normalizeEmail(email)
      return normalizedEmail !== '' && this.getOrganizerEmails().includes(normalizedEmail)
    },
    decodeHtml(html) {
      if (!html || typeof html !== 'string') {
        return ''
      }

      if (typeof document === 'undefined') {
        return html
      }

      const textarea = document.createElement('textarea')
      textarea.innerHTML = html
      return textarea.value
    },
    postProcessPaste(plugin, args) {
      const urlRe = /((https?:\/\/|www\.)[^\s<]+)/gi
      // only touch pure text runs, don't break existing tags
      const walk = (node) => {
        if (node.nodeType === 3) { // text node
          const text = node.nodeValue
          if (urlRe.test(text)) {
            const span = document.createElement('span')
            span.innerHTML = text.replace(urlRe, (m) => {
              const href = m.startsWith('http') ? m : 'https://' + m
              return `<a href="${href}" rel="noopener noreferrer" target="_blank">${m}</a>`
            })
            node.parentNode.replaceChild(span, node)
          }
        } else if (node.nodeType === 1 && node.childNodes) {
          Array.from(node.childNodes).forEach(walk)
        }
      }
      walk(args.node)
    },
    async applyCSSToAllElements() {
      const editor = this.$refs['editor-tiny']?.editor

      if (!editor) {
        return this.event.description.replace(/\n/g, '')
      }

      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = JSON.parse(JSON.stringify(this.event.description))

      // Recursively traverse elements and inherit parent styles if no explicit styling exists (same approach as SendMail - no content div)
      const editorBody = editor.getBody()
      if (!editorBody) {
        return tempDiv.innerHTML.replace(/\n/g, '')
      }

      const bodyComputedStyle = window.getComputedStyle(editorBody)
      tempDiv.style.fontSize = bodyComputedStyle.fontSize
      tempDiv.style.fontFamily = bodyComputedStyle.fontFamily
      tempDiv.style.color = bodyComputedStyle.color
      tempDiv.style.margin = '0'

      const processElement = (element: HTMLElement, parentComputedStyle = bodyComputedStyle) => {
        let computedStyle: CSSStyleDeclaration
        try {
          computedStyle = element.ownerDocument?.body?.contains(element) ? window.getComputedStyle(element) : parentComputedStyle
        } catch {
          computedStyle = parentComputedStyle
        }

        const children = element.children

        for (let i = 0; i < children.length; i++) {
          const child = children[i] as HTMLElement
          if (child.tagName?.toLowerCase() === 'br') {
            continue
          }

          const hasExplicitFontSize = child.style?.fontSize || child.getAttribute('size')
          const hasExplicitFontFamily = child.style?.fontFamily || child.getAttribute('face')
          const hasExplicitColor = child.style?.color

          let stylesToApply = ''

          if (!hasExplicitFontSize) {
            stylesToApply += `font-size: ${computedStyle.fontSize}; `
          }
          if (!hasExplicitFontFamily) {
            stylesToApply += `font-family: ${computedStyle.fontFamily}; `
          }
          if (!hasExplicitColor) {
            stylesToApply += `color: ${computedStyle.color}; `
          }

          if (stylesToApply) {
            const existingStyle = child.getAttribute('style') || ''
            child.style.cssText = `${existingStyle}; ${stylesToApply} margin: 0;`
          }

          processElement(child, computedStyle)
        }
      }

      processElement(tempDiv)

      return tempDiv.innerHTML.replace(/\n/g, '')
    },
    updateEditorContentStyle() {
      if (this.$refs['editor-tiny']) {
        const editor = this.$refs['editor-tiny'].editor
        this.$nextTick(() => {
          if (editor) {
            const bodyEditor = editor.getBody()
            if (bodyEditor) {
              bodyEditor.style.fontFamily = this.fontSetting ? this.fontSetting.family : 'Lato'
              bodyEditor.style.fontSize = (this.fontSetting ? this.fontSetting.size : 10) + 'pt'
            }
          }
        })
      }
    },
    textWithLinks(bodyText) {
      const textWithLinks = linkifyStr(bodyText, { target: '_blank' })
      return textWithLinks.replace(/\n/g, '<br>')
    },
    stripHtmlTags(text) {
      return text ? text.replace(/<[^>]*>?/gm, '') : ''
    },
    loadContacts(contactIds) {
      const ids = contactIds.filter(x => x && x !== '' && x !== null && x !== undefined)
      if (ids.length > 0) {
        ContactApi.contactResolve(ids)
        .then(response => {
          this.listContactAttendees = ((response.persons as (V2Person | Contactcontactv2Company)[]).concat(response.companies))
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
        })
      }
    },
    getContactName(list, id) {
      const contact = list.find(x => x.id === id)
      return contact ? (contact.name ? contact.name : contact.lastName + ' ' + contact.firstName) : '-'
    },
    redirectToDetailPageMiddle(id) {
      const contact = this.selectedEmployees.find(x => x.id === id || (('contactPersons') in x && x.contactPersons.some(cp => cp.id === id)))
      if (contact !== null) {
        let route = null
        if (this.isOwnerModule) {
          route = this.$router.resolve({
            name: 'OwnerContactDetailsView',
            params: { id: contact.id }
          })
        } else if (this.isAssetModule) {
          route = this.$router.resolve({
            name: 'AssetContactDetailsView',
            params: { id: contact.id }
          })
        } else if (this.isAccountingModule) {
          route = this.$router.resolve({
            name: 'AccountingContactDetailsView',
            params: { id: contact.id }
          })
        } else if (this.isMarketingModule) {
          route = this.$router.resolve({
            name: 'MarketingContactDetailsView',
            params: { id: contact.id }
          })
        } else {
          route = this.$router.resolve({
            name: 'AdminContactDetailsView',
            params: { id: contact.id }
          })
        }
        window.open(route.href)
      }
    },
    redirectToDetailPage(id) {
      const contact = this.selectedEmployees.find(x => x.id === id || (('contactPersons') in x && x.contactPersons.some(cp => cp.id === id)))
      if (contact) {
        if (this.isOwnerModule) {
          this.$router.push({
            name: 'OwnerContactDetailsView',
            params: { id: contact.id }
          })
        } else if (this.isAssetModule) {
          this.$router.push({
            name: 'AssetContactDetailsView',
            params: { id: contact.id }
          })
        } else if (this.isAccountingModule) {
          this.$router.push({
            name: 'AccountingContactDetailsView',
            params: { id: contact.id }
          })
        } else if (this.isMarketingModule) {
          this.$router.push({
            name: 'MarketingContactDetailsView',
            params: { id: contact.id }
          })
        } else {
          this.$router.push({
            name: 'AdminContactDetailsView',
            params: { id: contact.id }
          })
        }
      }
    },
    redirectToRequest() {
      if (this.isMarketingModule && (this.additionalReferences?.split(',').find(x => x.includes('req')) || this.event.referenceIds?.find(x => x.includes('req'))) && (this.objectReference || this.object)) {
        this.$router.push({
          name: 'MarketingObjectDetailsView',
          params: { id: this.objectReference?.id || this.object?.id },
          query: {
            view: 0,
            elementSelected: this.additionalReferences?.split(',').find(x => x.includes('req')) || this.event.referenceIds?.find(x => x.includes('req'))
          }
        })
      }
    },
    redirectToRequestMiddleClick() {
      if (this.isMarketingModule && (this.additionalReferences?.split(',').find(x => x.includes('req')) || this.event.referenceIds?.find(x => x.includes('req'))) && (this.objectReference || this.object)) {
        const route = this.$router.resolve({
          name: 'MarketingObjectDetailsView',
          params: { id: this.objectReference?.id || this.object?.id },
          query: {
            view: 0,
            elementSelected: this.additionalReferences?.split(',').find(x => x.includes('req')) || this.event.referenceIds?.find(x => x.includes('req'))
          }
        })
        window.open(route.href)
      }
    },
    getLocation(location) {
      let addressObject = null
      if (location === 'onSite') {
        if (this.additionalReferences !== '') {
          const listReferences = this.additionalReferences.split(',')
          if (listReferences.length > 0) {
            if (listReferences[0].startsWith('contact')) {
              addressObject = this.contactsList.find(contact => contact.id === listReferences[0]).address || null
            } else if (listReferences[0].startsWith('request')) {
              addressObject = this.objectReference?.address || null
            }
          }
        } else if (this.eventDetail && this.listSelectedAttendees.length > 0) {
          const attendees = this.listSelectedAttendees.filter(x => x.email !== this.eventDetail.organizer.email)
          if (attendees.length === 1) {
            const contact = this.listContactAttendees.find(contact => contact.id === attendees[0].id)
            if (contact) {
              addressObject = contact.address || null
            }
          } else if (attendees.length > 1 && this.eventDetail.referenceIds.length > 0) {
            if (this.eventDetail.referenceIds[0].startsWith('contact')) {
              addressObject = this.listReferences.find(contact => contact.id === this.eventDetail.referenceIds[0])?.address || null
            }
          }
        }
      } else if (location === 'inOffice' && this.userContact) {
        addressObject = this.userContact.address || null
      }
      if (addressObject) {
        const addr = (addressObject.street ? addressObject.street + (addressObject.streetNumber ? ' ' + addressObject.streetNumber : '') : '') +
          (addressObject.zip ? ', ' + addressObject.zip + ' ' : '') +
          (addressObject.city ? addressObject.city : '')

        if (addr) {
          this.event.location = addr
        }
      } else {
        this.event.location = ''
      }
    },

    changeNotifiedEmail(contact) {
      if (contact.selectedContactPerson) {
        if (contact.contactPersons && contact.contactPersons.length > 0) {
          const contactPerson = contact.contactPersons.find(item => item.id === contact.selectedContactPerson)
          if (contactPerson) {
            contact.email = contactPerson.email ? contactPerson.email : ''
          }
        }
      } else {
        const item = this.selectedEmployees.find(item => item.id === contact.id || item.objectID === contact.id)
        if (item) {
          contact.email = item.email
        }
      }
    },
    async updateAttendeeEmails() {
      // Check if attendees email updated and update the contact or contact person
      const updatePromises = this.listSelectedAttendees.map(async (x) => {
        if (!x.selectedContactPerson) {
          // Regular contact - check if email was updated
          const contact = this.listContactAttendees.find(c => c.id === x.id || c.objectID === x.id)
          if (contact && x.email && x.email !== contact.email) {
            contact.types = contact.types.length > 0 ? contact.types.map(type => type.toUpperCase()) : []
            const contactToUpdate = { ...contact, email: x.email }
            try {
              const response = await ContactApi.editContact(contactToUpdate)
              console.log(response)
            } catch (e) {
              console.log(e)
              Vue.toasted.show(this.$tc('message.savingErrors.editContact'), { type: 'error' })
            }
          }
        } else {
          // Contact person - check if contact person email was updated
          const contactPerson = x.contactPersons?.find(cp => cp.id === x.selectedContactPerson)
          if (contactPerson && x.email && x.email !== contactPerson.email) {
            const companyId = x.id || x.objectID
            const contactPersonToUpdate = { ...contactPerson, email: x.email }
            try {
              await ContactApi.editContactPerson(contactPersonToUpdate, companyId, x.selectedContactPerson)
            } catch (e) {
              console.log(e)
              Vue.toasted.show(this.$tc('message.savingErrors.editContact'), { type: 'error' })
            }
          }
        }
      })
      await Promise.all(updatePromises)
    },
    listEmails(id) {
      const item = this.selectedEmployees.find(item => item.id === id || item.objectID === id)
      const array = []
      if (item) {
        if (item?.email !== '') {
          array.push(item.email)
        }
        if (item?.workEmail && item.workEmail?.use && item.workEmail?.email !== '') {
          array.push(item.workEmail.email)
        }
      }
      return array
    },
    objectUrl() {
      const object = this.objectReference || this.object
      if (object) {
        if (this.isAdminModule || (this.isMarketingModule && this.hasAccessAdminBase)) {
          this.$router.push({
            name: 'AdminBuildingDetailsView',
            params: { id: object.buildingId },
            query: {
              view: 'objects',
              elementSelected: object.id || object.objectID
            }
          })
        } else if (this.isOwnerModule || (this.isMarketingModule && this.hasAccessOwnerBase)) {
          this.$router.push({
            name: 'OwnerBuildingDetailsView',
            params: { id: object.buildingId },
            query: {
              view: 'objects',
              elementSelected: object.id || object.objectID
            }
          })
        } else if (this.isAssetModule || (this.isMarketingModule && this.hasAccessAssetBase)) {
          this.$router.push({
            name: 'AssetBuildingDetailsView',
            params: { id: object.buildingId },
            query: {
              view: 'objects',
              elementSelected: object.id || object.objectID
            }
          })
        } else if (this.isAccountingModule || (this.isMarketingModule && this.hasAccessAccountingBase)) {
          this.$router.push({
            name: 'AccountingBuildingDetailsView',
            params: { id: object.buildingId },
            query: {
              view: 'objects',
              elementSelected: object.id || object.objectID
            }
          })
        }
      }
      return '#'
    },
    objectUrlMiddleClick() {
      const object = this.objectReference || this.object
      if (object) {
        let routeData = null
        if (this.isAdminModule || (this.isMarketingModule && this.hasAccessAdminBase)) {
          routeData = this.$router.resolve({
            name: 'AdminBuildingDetailsView',
            params: { id: object.buildingId },
            query: {
              view: 'objects',
              elementSelected: object.id || object.objectID
            }
          })
        } else if (this.isOwnerModule || (this.isMarketingModule && this.hasAccessOwnerBase)) {
          routeData = this.$router.resolve({
            name: 'OwnerBuildingDetailsView',
            params: { id: object.buildingId },
            query: {
              view: 'objects',
              elementSelected: object.id || object.objectID
            }
          })
        } else if (this.isAssetModule || (this.isMarketingModule && this.hasAccessAssetBase)) {
          routeData = this.$router.resolve({
            name: 'AssetBuildingDetailsView',
            params: { id: object.buildingId },
            query: {
              view: 'objects',
              elementSelected: object.id || object.objectID
            }
          })
        } else if (this.isAccountingModule || (this.isMarketingModule && this.hasAccessAccountingBase)) {
          routeData = this.$router.resolve({
            name: 'AccountingBuildingDetailsView',
            params: { id: object.buildingId },
            query: {
              view: 'objects',
              elementSelected: object.id || object.objectID
            }
          })
        }
        if (routeData) {
          window.open(routeData.href, '_blank')
        }
      }
      return '#'
    },
    imageClicked(item) {
      if (item.url && item.url !== '') {
        this.galleryImages = [
          item.url
        ]
        this.$nextTick(function () {
          this.$viewerApi({
            images: this.galleryImages,
            options: this.viewerOptions
          })
        })
      }
    },
    openVideo(media) {
      this.selectedObjectVideo = media.url
      this.$nextTick(() => {
        this.$refs['show-video'].show()
      })
    },
    async addFile(event) {
      this.loading2 = true
      this.attachment = await this.getFilesFromEvent(event)
      if (this.attachment && this.attachment.length > 0) {
        await this.sendFile()
        this.$store.dispatch('user/setChangedModal', true).then().catch(e => console.log(e))
      }
    },
    deleteMedia(e, id) {
      e.stopPropagation()
      if (this.mediasList) {
        this.mediasList = this.mediasList.filter(el => el.id !== id)
        this.mediaUpdated = true
      }
    },
    async sendFile() {
      this.uploading = true
      const response = await this.uploadMedia(this.attachment)
      if (response?.length > 0) {
        for (const media of response) {
          this.mediasList.push(media)
        }
        this.$nextTick(() => {
          this.attachment = []
          this.uploading = false
          this.mediaUpdated = true
          this.$nextTick(() => {
            this.$refs?.attachmentUpload?.resetPlaceholder()
          })
        })
      }
      this.uploading = false
    },
    changeAttendeesStatus() {
      this.listSelectedAttendees.forEach(x => {
        if (this.isOrganizerEmail(x.email)) {
          x.status = 'ATTENDEE_STATUS_ACCEPTED'
        } else {
          x.status = 'ATTENDEE_STATUS_NEEDS_ACTION'
        }
      })
    },
    renderTemplate() {
      const objectId = this.objectReference ? this.objectReference.id : this.object ? this.object.id : ''
      MessageApi.renderTemplate(this.templateId.id, '', objectId)
        .then(response => {
          this.mediasList = []
          this.event.title = this.decodeHtmlEntitiesIfNeeded(response.subject)
          this.event.description = response.text
          if (response.attachmentMediaIds.length > 0) {
            MediaApi.getListMedia(response.attachmentMediaIds)
              .then(response => {
                for (const media of response.media) {
                  this.mediasList.push(media)
                }
              })
              .catch(() => {
                Vue.toasted.show(this.$t('message.loadingErrors.media'), { type: 'error' })
              })
          }
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.renderTemplate'), { type: 'error' })
        })
    },
    showDeleteConfirmationModal() {
      this.$refs['delete-confirmation-modal'].show()
    },
    newAttendeesStatusEditClicked(item) {
      const attendeeIndex = this.listSelectedAttendees.findIndex(x => x.email === item || x.name === item)
      if (attendeeIndex !== -1) {
        this.listSelectedAttendees[attendeeIndex].statusEdit = true
      }
    },
    labelFormatDate(date) {
      if (date) {
       return date.substr(8, 2) + '.' + date.substr(5, 2) + '.' + date.substr(0, 4)
      } else {
        return '-'
      }
    },
    labelFormatTime(time) {
      if (this.event.allDay) {
        return '-'
      } else {
        return time
      }
    },
    formatTimeForCalendar(time) {
      if (time === null) {
        return '-'
      }
      // start and end need to be in format the calendar package can understand
      return (time.hours.toString().length === 2 ? time.hours.toString() : '0' + time.hours.toString()) + ':' + (time.minutes.toString().length === 2 ? time.minutes.toString() : '0' + time.minutes.toString())
    },
    getListTimes() {
      let hours, minutes
      const list = []
      for (let i = 0; i <= 1425; i += 15) {
        hours = Math.floor(i / 60)
        minutes = i % 60
        if (minutes < 10) {
          minutes = '0' + minutes // adding leading zero
        }
        if (hours < 10) {
          hours = '0' + hours // adding leading zero
        }
        if (hours === 24) {
          hours = '00'
        }
        list.push(hours + ':' + minutes)
      }
      this.timeList = list
    },
    editEvent(allEvent = false, attendee = null) {
      if (this.eventDetail && this.eventDetail.masterEventId !== '') {
        if (!allEvent && attendee) {
          this.attendeeToEdit = attendee
        }
        this.$refs.confirmationRecurrence?.show()
      } else {
        if (allEvent) {
          this.isEditable = true
        } else {
          this.newAttendeesStatusEditClicked(attendee.email !== '' ? attendee.email : attendee.name ? attendee.name : attendee.lastName + '_' + attendee.firstName)
        }
      }
    },
    acceptEditRecurringEvent() {
      this.$refs.confirmationRecurrence?.hide()
      if (this.newStatusButton !== '') {
        this.editAttendeeStatus(this.newStatusButton)
      } else if (this.attendeeToEdit) {
        this.newAttendeesStatusEditClicked(this.attendeeToEdit.email !== '' ? this.attendeeToEdit.email : this.attendeeToEdit.name ? this.attendeeToEdit.name : this.attendeeToEdit.lastName + '_' + this.attendeeToEdit.firstName)
      } else {
        this.isEditable = true
      }
    },
    hideConfirmationRecurrenceModal() {
      this.$refs.confirmationRecurrence?.hide()
      this.attendeeToEdit = null
      this.newStatusButton = false
    },
    updateStatusButtonsClicked(status) {
      if (this.eventDetail.masterEventId !== '') {
        this.$refs.confirmationRecurrence?.show()
        this.newStatusButton = status
      } else {
        this.editAttendeeStatus(status)
      }
    },
    checkNewEvent() {
      this.showCompanyAttendeesValidation = true
      this.invalidCompanyAttendeeKeys = new Set()
      if (!this.validateCompanyAttendeesSelection()) {
        return
      }
      this.$v.event.$touch()
      this.$v.listSelectedAttendees.$touch()

      if (!this.$v.event.$invalid && !this.$v.listSelectedAttendees.$invalid) {
        this.newEvent()
      } else if (this.$v.event.endDate.$dirty && this.$v.event.endDate.$error && !this.$v.event.endDate.dateOverlaps) {
        Vue.toasted.show(this.$t('message.savingErrors.dateOverlaps'), { type: 'error' })
      } else if ((this.$v.event.startDate.$dirty && this.$v.event.startDate.$error && !this.$v.event.startDate.required) || (this.$v.event.endDate.$dirty && this.$v.event.endDate.$error && !this.$v.event.endDate.required)) {
        Vue.toasted.show(this.$t('message.savingErrors.invalidDate'), { type: 'error' })
      } else if (this.$v.event.endDate.$dirty && this.$v.event.endDate.$error) {
        Vue.toasted.show(this.$t('message.savingErrors.endTimeAfterStart'), { type: 'error' })
      }
    },
    validateCompanyAttendeesSelection() {
      const duplicatedCompanyAttendees = this.getDuplicatedCompanyAttendees()
      if (duplicatedCompanyAttendees.length === 0) {
        this.showCompanyAttendeesValidation = false
        this.invalidCompanyAttendeeKeys = new Set()
        return true
      }

      const companyAttendeesValidation = this.getCompanyAttendeesValidation()
      const missingContactPerson = companyAttendeesValidation.missingContactPersons.size > 0
      if (missingContactPerson) {
        this.invalidCompanyAttendeeKeys = this.buildInvalidCompanyAttendeeKeys(companyAttendeesValidation.missingContactPersons)
        Vue.toasted.show(this.$t('message.savingErrors.duplicateCompanyMissingContactPerson'), { type: 'error' })
        return false
      }

      const hasDuplicateContactPersons = companyAttendeesValidation.duplicatedContactPersons.size > 0
      if (hasDuplicateContactPersons) {
        this.invalidCompanyAttendeeKeys = this.buildInvalidCompanyAttendeeKeys(companyAttendeesValidation.duplicatedContactPersons)
        Vue.toasted.show(this.$t('message.savingErrors.duplicateCompanyDuplicateContactPerson'), { type: 'error' })
        return false
      }

      this.showCompanyAttendeesValidation = false
      this.invalidCompanyAttendeeKeys = new Set()
      return true
    },
    getCompanyAttendeesValidation() {
      const duplicatedCompanyAttendees = this.getDuplicatedCompanyAttendees()
      const missingContactPersons = new Set()
      const duplicatedContactPersons = new Set()
      duplicatedCompanyAttendees.forEach(attendee => {
        if (!attendee.selectedContactPerson) {
          missingContactPersons.add(attendee)
        }
      })
      duplicatedCompanyAttendees.forEach((attendee, index) => {
        if (!attendee.selectedContactPerson) {
          return
        }
        const hasDuplicateContactPerson = duplicatedCompanyAttendees.findIndex(candidate =>
          candidate.id === attendee.id &&
            candidate.selectedContactPerson === attendee.selectedContactPerson
        ) !== index
        if (hasDuplicateContactPerson) {
          duplicatedContactPersons.add(attendee)
        }
      })
      return {
        missingContactPersons,
        duplicatedContactPersons
      }
    },
    getAttendeeCompanyKey(attendee) {
      return attendee.id || attendee.objectID || null
    },
    getAttendeeValidationKey(attendee) {
      const companyKey = this.getAttendeeCompanyKey(attendee) || attendee.name
      return `${companyKey || ''}::${attendee.selectedContactPerson || 'none'}::${attendee.email || ''}`
    },
    buildInvalidCompanyAttendeeKeys(attendeesSet) {
      const keys = new Set<string>()
      attendeesSet.forEach(attendee => {
        keys.add(this.getAttendeeValidationKey(attendee))
      })
      return keys
    },
    isInvalidContactPerson(attendee) {
      if (!this.showCompanyAttendeesValidation) {
        return false
      }
      return this.invalidCompanyAttendeeKeys.has(this.getAttendeeValidationKey(attendee))
    },
    getContactPersonValidationClass(attendee) {
      return this.isInvalidContactPerson(attendee) ? 'red-border' : 'default-border'
    },
    getDuplicatedCompanyAttendees() {
      const companyAttendees = this.listSelectedAttendees.filter(attendee =>
        this.getAttendeeCompanyKey(attendee) &&
        attendee.contactPersons &&
        attendee.contactPersons.length > 0
      )
      const groupedByCompany = companyAttendees.reduce((acc, attendee) => {
        const companyKey = this.getAttendeeCompanyKey(attendee)
        if (!companyKey) {
          return acc
        }
        if (!acc[companyKey]) {
          acc[companyKey] = []
        }
        acc[companyKey].push(attendee)
        return acc
      }, {} as Record<string, typeof companyAttendees>)
      return (Object.values(groupedByCompany) as typeof companyAttendees[])
        .filter(group => group.length > 1)
        .flat()
    },
    async newEvent() {
      this.loading = true
      try {
        // Deep clone event object
        const eventToCreate = JSON.parse(JSON.stringify(this.event))
        eventToCreate.description = await this.applyCSSToAllElements()
        // Handle all-day event
        if (eventToCreate.allDay) {
          eventToCreate.startTime = null
          eventToCreate.endTime = null
        }

        // Helper to parse date string to object
        const parseDateString = (dateStr) => ({
          year: parseInt(dateStr.substr(0, 4)),
          month: parseInt(dateStr.substr(5, 2)),
          day: parseInt(dateStr.substr(8, 2))
        })
        // Helper to parse time string to object
        const parseTimeString = (timeStr) => timeStr ? { hours: parseInt(timeStr.substr(0, 2)), minutes: parseInt(timeStr.substr(3, 2)), seconds: 0, nanos: 0 } : null

        eventToCreate.startDate = parseDateString(eventToCreate.startDateString)
        eventToCreate.endDate = parseDateString(eventToCreate.endDateString)
        eventToCreate.startTime = !eventToCreate.allDay ? parseTimeString(eventToCreate.startTimeString) : null
        eventToCreate.endTime = !eventToCreate.allDay ? parseTimeString(eventToCreate.endTimeString) : null

        // Update attendee emails if changed
        await this.updateAttendeeEmails()

        // Build attendees array
        const attendees = this.listSelectedAttendees.map(x => ({
          displayName: x.name,
          email: x.email,
          contactId: x.selectedContactPerson ? x.selectedContactPerson : x.id ? x.id : x.objectID,
          status: x.status !== '' ? x.status : 'ATTENDEE_STATUS_NEEDS_ACTION'
        })) || this.eventDetail.attendees

        // Set userId
        eventToCreate.userId = this.organizer ? this.organizer.id : this.eventDetail ? this.eventDetail.userId : ''

        // Build referenceIds
        const getReferenceIds = () => {
          const attendeeIds = this.listSelectedAttendees.map(x => x.id || x.objectID).filter(y => y != null)
          if (this.additionalReferences !== '') {
            const allReferences = attendeeIds.concat(this.additionalReferences.split(',')).filter(y => y != null)
            if (this.eventDetail === null) {
              return [...new Set(allReferences)]
            } else {
              return this.eventDetail.referenceIds.concat([...new Set(allReferences)])
            }
          } else {
            if (this.eventDetail === null) {
              return attendeeIds
            } else {
              return this.eventDetail.referenceIds.concat(attendeeIds).filter(y => y != null)
            }
          }
        }
        eventToCreate.referenceIds = getReferenceIds()

        // Set mediaIds
        eventToCreate.mediaIds = this.mediasList.map(x => x.id)

        // Recurrence Data
        if (eventToCreate.recurrenceStartDateString) {
          eventToCreate.recurrenceStartDate = parseDateString(this.event.recurrenceStartDateString)
        }
        if (eventToCreate.recurrenceEndDateString) {
          eventToCreate.recurrenceEndDate = parseDateString(this.event.recurrenceEndDateString)
        }

        // Recurrence end date calculation
        let endDateWithRecurrence = null
        if (eventToCreate.occurrenceCount !== 0 && this.endDateOccurrencesResponse) {
          const dateItem = new Date(`${this.endDateOccurrencesResponse.year}/${this.endDateOccurrencesResponse.month}/${this.endDateOccurrencesResponse.day}`)
          switch (eventToCreate.frequency) {
            case 'EVENT_FREQUENCY_DAILY':
              endDateWithRecurrence = dateItem.setDate(dateItem.getDate() + parseInt(eventToCreate.occurrenceCount))
              break
            case 'EVENT_FREQUENCY_WEEKLY':
              endDateWithRecurrence = dateItem.setDate(dateItem.getDate() + (parseInt(eventToCreate.occurrenceCount) * 7))
              break
            case 'EVENT_FREQUENCY_MONTHLY':
              endDateWithRecurrence = dateItem.setMonth(dateItem.getMonth() + parseInt(eventToCreate.occurrenceCount))
              break
            case 'EVENT_FREQUENCY_YEARLY':
              endDateWithRecurrence = dateItem.setFullYear(dateItem.getFullYear() + parseInt(eventToCreate.occurrenceCount))
              break
            default:
              break
          }
        }
        eventToCreate.startDateOccurrencesResponse = new Date(this.$options.filters.objectToDateInput(this.startDateOccurrencesResponse)) < new Date(this.$options.filters.objectToDateInput(eventToCreate.startDate)) ? this.startDateOccurrencesResponse : eventToCreate.startDate
        let endDate = new Date(this.$options.filters.objectToDateInput((new Date(this.$options.filters.objectToDateInput(this.endDateOccurrencesResponse)) > new Date(this.$options.filters.objectToDateInput(eventToCreate.endDateObject)) ? this.endDateOccurrencesResponse : eventToCreate.endDateObject)))
        endDate.setDate(endDate.getDate() + 1)
        eventToCreate.endDateOccurrencesResponse = this.toObject(endDate)

        if (attendees.length > 0 && this.mediasList.length > 0) {
          const ids = this.mediasList.map(x => x.id)
          MediaApi.setPublicFlag(ids, true).catch(error => {
            console.log(error)
          })
        }
        // Check for changes in recurrence properties
        if (this.eventDetail !== null && this.source !== 'email') {
          if (
            new Date(this.$options.filters.objectToDateInput(this.initialEventDetail.startDate)).getTime() !== new Date(eventToCreate.startDateString).getTime() ||
            new Date(this.$options.filters.objectToDateInput(this.initialEventDetail.endDate)).getTime() !== new Date(eventToCreate.endDateString).getTime() ||
            this.initialEventDetail.frequency !== eventToCreate.frequency ||
            new Date(this.$options.filters.objectToDateInput(this.initialEventDetail.recurrenceEndDate)).getTime() !== new Date(eventToCreate.recurrenceEndDateString).getTime() ||
            this.initialEventDetail.occurrenceCount !== eventToCreate.occurrenceCount
          ) {
            eventToCreate.resetOccurrencesEvents = true
          }
          if (this.editAllRecurringEvents && eventToCreate.recurrenceStartDate && !eventToCreate.startDate) {
            eventToCreate.startDate = eventToCreate.recurrenceStartDate
          }
          if (this.editAllRecurringEvents && eventToCreate.recurrenceEndDate && !eventToCreate.endDate) {
            eventToCreate.endDate = eventToCreate.recurrenceEndDate
          }
        }
        // If editing an existing event (not from email)
        if (this.eventDetail !== null && this.source !== 'email') {
          if (this.eventDetail.masterEventId !== '' && eventToCreate.occurrenceCount !== 0) {
            eventToCreate.recurrenceEndDate = null
          }
          await CalendarApi.updateEvent(
            eventToCreate,
            this.$store.getters['user/getAccountId'],
            attendees,
            eventToCreate.referenceIds,
            this.editAllRecurringEvents ? this.eventDetail.masterEventId : ''
          ).then(response => {
            this.$store.dispatch('user/setChangedModal', false).catch(e => console.log(e))
            this.hide()
            if (this.eventDetail.masterEventId !== '' || this.event.recurringEvent || response.event?.recurrenceStartDate !== null) {
              this.$emit('reload-events', this.eventDetail)
            } else {
              const event: LocalEvent = {
                ...response.event,
                ownerId: this.eventDetail.ownerId || '',
                notifyParticipants: this.event.notifyParticipants
              }
              event.attendees = attendees
              event.allDay = eventToCreate.allDay
              this.$emit('post-creation', [event])
            }
          })
        } else {
          // Creating a new event
          const calendar = this.availableCalendars.find(x => x.userId === this.organizer?.id)
          if (calendar?.nylasCalendarId) {
            await CalendarApi.createEvent(
              eventToCreate,
              this.$store.getters['user/getAccountId'],
              attendees,
              calendar.nylasCalendarId
            ).then(response => {
              this.$store.dispatch('user/setChangedModal', false).catch(e => console.log(e))
              this.hide()
              const event: LocalEvent = {
                ...response.event,
                ownerId: this.accountId,
                notifyParticipants: this.event.notifyParticipants
              }
              if (event) {
                if (event.masterEventId !== '' || this.event.recurringEvent) {
                  this.$emit('reload-events', event)
                  if (event.recurrenceEndDate) {
                    this.endingRecurrence = 'endByDateRecurrence'
                  } else if (event.occurrenceCount !== 0) {
                    this.endingRecurrence = 'endAfter'
                  }
                } else {
                  event.attendees = attendees
                  event.organizer = {
                    email: calendar.associatedEmail,
                    name: this.getOrganizer || response.event?.organizer?.name || ''
                  }
                  event.allDay = eventToCreate.allDay
                  this.$emit('post-creation', [event])
                }
              } else {
                this.$emit('reload-events')
              }
            })
          } else {
            Vue.toasted.show(this.$t('message.savingErrors.calendar'), { type: 'error' })
          }
        }
      } catch (e) {
        console.error(e)
        Vue.toasted.show(this.$t('message.savingErrors.calendar'), { type: 'error' })
      } finally {
        this.loading = false
      }
    },
    show() {
      this.keyRefresh += 1
      if (this.$refs['modal-add-event']) {
        this.$refs['modal-add-event'].show()
      }
      this.keyRefresh += 1
    },
    hide() {
      this.keyRefresh += 1
      if (this.$refs['modal-add-event']) {
        this.$refs['modal-add-event'].hide()
      }
      this.keyRefresh += 1
      this.$emit('hide-modal')
    },
    hideModal() {
      if (this.isChanged && this.isEditable) {
        this.$refs.globalConfirmationModal.show()
      } else {
        this.hide()
      }
    },
    checkEnter() {
      // eslint-disable-next-line no-useless-escape
      const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      this.searchPartipantValidation = re.test(String(this.searchPartipant).toLowerCase())
      if (this.searchPartipantValidation) {
        if (this.searchPartipant !== '') {
          this.$refs.autosuggest.internalValue = this.searchPartipant
          // select the first item in the list of suggestions if there is one and the user presses enter key on the input field, if not, the user will be able to add the email address as a new attendee
          if (this.$refs.autosuggest.suggestions.length > 0 && this.$refs.autosuggest.suggestions[0].data.length > 0) {
            this.selectHandler({ item: this.$refs.autosuggest.suggestions[0].data[0] })
          } else {
            if (this.isOrganizerEmail(this.searchPartipant)) {
              this.searchPartipant = ''
              this.$refs.autosuggest.internalValue = ''
              Vue.toasted.show(this.$t('message.loadingErrors.addAttendeeWithOrganizerEmail'), { type: 'error' })
              return
            }
            this.selectedEmployees.push({
              name: this.searchPartipant,
              email: this.searchPartipant,
              id: null,
              status: 'ATTENDEE_STATUS_NEEDS_ACTION',
              contactPersons: null,
              selectedContactPerson: null,
              statusEdit: false
            })
            this.listSelectedAttendees.push({
              name: this.searchPartipant,
              email: this.searchPartipant,
              id: null,
              status: 'ATTENDEE_STATUS_NEEDS_ACTION',
              contactPersons: null,
              selectedContactPerson: null,
              statusEdit: false
            })
          }
        }
        this.searchPartipant = ''
        this.searchPartipantValidation = true
      }
    },
    closeCurrentModal() {
      this.$refs.globalConfirmationModal.hide()
      this.$store.dispatch('user/setChangedModal', false).then().catch(e => console.log(e))
      this.hide()
    },
    selectHandler(selected) {
      if (selected && selected.item) {
        if (this.isOrganizerEmail(selected.item.email) || (this.organizer?.id && (selected.item.id === this.organizer.id || selected.item.objectID === this.organizer.id))) {
          this.searchPartipant = ''
          this.$refs.autosuggest.internalValue = ''
          this.searchPartipantValidation = true
          Vue.toasted.show(this.$t('message.loadingErrors.addAttendeeWithOrganizerEmail'), { type: 'error' })
          return
        }
        const isCompanySelection = 'contactPersons' in selected.item
        if (selected.item.email === '') {
          const attendeesAlreadyExist = isCompanySelection || this.listSelectedAttendees.filter(x => x.name === selected.item.name).length === 0
          if (attendeesAlreadyExist) {
            this.selectedEmployees.push(selected.item)
            this.listSelectedAttendees.push({
              name: selected.item.name,
              email: selected.item.email,
              id: selected.item.id || selected.item.objectID,
              status: 'ATTENDEE_STATUS_NEEDS_ACTION',
              contactPersons: 'contactPersons' in selected.item && selected.item.contactPersons?.length > 0 ? selected.item.contactPersons.filter(cp => cp.active) : null,
              selectedContactPerson: null,
              statusEdit: false
            })

            this.$nextTick(function () {
              this.searchPartipant = ''
              this.$refs.autosuggest.internalValue = ''
              this.searchPartipantValidation = true
            })
          }
        } else if (!(this.isOrganizerEmail(selected.item.email) || (this.organizer?.id && (selected.item.id === this.organizer.id || selected.item.objectID === this.organizer.id)))) {
          const attendeesAlreadyExist = isCompanySelection || this.listSelectedAttendees.filter(x => x.email === selected.item.email).length === 0
          if (attendeesAlreadyExist) {
            this.selectedEmployees.push(selected.item)
            this.listSelectedAttendees.push({
              name: selected.item.name,
              email: selected.item.email,
              id: selected.item.id || selected.item.objectID,
              status: 'ATTENDEE_STATUS_NEEDS_ACTION',
              contactPersons: 'contactPersons' in selected.item && selected.item.contactPersons?.length > 0 ? selected.item.contactPersons.filter(cp => cp.active) : null,
              selectedContactPerson: null,
              statusEdit: false
            })

            this.$nextTick(function () {
              this.searchPartipant = ''
              this.$refs.autosuggest.internalValue = ''
              this.searchPartipantValidation = true
            })
          }
        } else {
          this.$nextTick(function () {
            this.searchPartipant = ''
            this.$refs.autosuggest.internalValue = ''
            this.searchPartipantValidation = true
          })
        }
        if (!this.listContactAttendees.find(x => x.id === selected.item.id)) {
          selected.item.id = selected.item.objectID || selected.item.id
          this.listContactAttendees.push(selected.item)
        }
      }
    },
    removeInvitation(item) {
      const indexToDelete = this.listSelectedAttendees.findIndex(element =>
        (item.id && element.id === item.id && element.selectedContactPerson === item.selectedContactPerson && element.email === item.email) ||
        (!item.id && item.email !== '' && element.email === item.email) ||
        (!item.id && item.email === '' && item.name && element.name === item.name)
      )
      if (indexToDelete !== -1) {
        this.listSelectedAttendees.splice(indexToDelete, 1)
      }
    },
    getSuggestionValue(suggestion) {
      return suggestion.item.name
    },
    deleteEvent(type) {
      this.$store.dispatch('user/setChangedModal', false).then().catch(e => console.log(e))

      const eventId = type === 'all' && this.eventDetail.masterEventId !== '' ? this.eventDetail.masterEventId : this.eventDetail.id || this.event.id

      CalendarApi.deleteEvent(eventId, this.eventDetail.userId)
        .then(() => {
          this.$refs['delete-confirmation-modal']?.hide()
          this.hide()
          if (this.eventDetail.masterEventId !== '' && type === 'all') {
            this.$emit('reload-events', this.eventDetail)
          } else {
            this.$emit('remove-event', this.eventDetail, type === 'single')
          }
          Vue.toasted.show(this.$t('message.successMessages.eventDeleted'), { type: 'success' })
        })
        .catch(e => {
          console.log(e)
          this.loading = false
          Vue.toasted.show(this.$t('message.savingErrors.calendar'), { type: 'error' })
        })
    },
    hitsToSuggestions(hits) {
      const contactPersons = [].concat(...hits.map(x => x.contactPersons ? x.contactPersons.filter(cp => cp.active) : []))
      const organizerEmails = this.getOrganizerEmails()
      const filtered = hits.filter(x => !organizerEmails.includes(x.email) && !(this.organizer?.id && (x.id === this.organizer.id || x.objectID === this.organizer.id)))
      const referencePersons = this.reference && this.reference.contactPersons ? this.reference.contactPersons : []
      return [{ data: filtered.concat(referencePersons).concat(contactPersons) }]
    },
    onChange(refine, email) {
      this.searchPartipantValidation = true
      refine(email)
    },
    editAttendeeStatus(action) {
      let status = ''
      if (action === 'accept') {
        status = 'ATTENDEE_STATUS_ACCEPTED'
      } else {
        status = 'ATTENDEE_STATUS_DECLINED'
      }
      if (this.organizer?.email || this.eventDetail.organizer?.email) {
          const attendeeEmail =
            this.listSelectedAttendees.find(x => this.isCurrentUserEmail(x.email))?.email ||
            this.eventDetail?.attendees?.find(x => this.isCurrentUserEmail(x.email))?.email ||
            ''
          if (attendeeEmail !== '') {
            const element = this.listSelectedAttendees.findIndex(x => this.isCurrentUserEmail(x.email))
            if (element !== -1) {
              this.listSelectedAttendees[element].status = status
            }
            this.onEditStatusAttendeeClicked(attendeeEmail, status)
          }
      }
    },
    onEditStatusAttendeeClicked(email, fallbackStatus = null) {
      if (!this.eventDetail) {
        const attendeeIndex = this.listSelectedAttendees.findIndex(x => x.email === email || x.name === email)
        if (attendeeIndex !== -1) {
          this.listSelectedAttendees[attendeeIndex].statusEdit = false
        }
      } else {
        if (this.isCurrentUserEmail(this.eventDetail.organizer?.email)) {
          this.newEvent()
        } else {
          this.statusUpdating = true
          const eventId = this.editAllRecurringEvents ? this.eventDetail.masterEventId : this.eventDetail.id
          const attendeeIndex = this.listSelectedAttendees.findIndex(x => x.email === email || x.name === email)
          const statusToSend = attendeeIndex !== -1 ? this.listSelectedAttendees[attendeeIndex].status : fallbackStatus
          if (statusToSend) {
            CalendarApi.updateStatusAttendee(this.calendarOfUser?.nylasCalendarId, eventId, this.userId, statusToSend)
              .then(() => {
                this.$emit('attendees-changed')
              })
              .catch(error => {
                console.error(error)
              }).finally(() => {
                this.hide()
                this.statusUpdating = false
              })
          }
        }
      }
    },
    hasWorkEmail(id) {
      const item = this.selectedEmployees.find(item => item.id === id || item.objectID === id)
      return this.listEmails(id).length > 0 && item.workEmail && item.workEmail.use && item.workEmail.email !== ''
    },
    getAttendeeEmailValidationState(attendee) {
      if (!this.isEditable) return null
      const index = this.listSelectedAttendees.findIndex(item =>
        (item.id && attendee.id && item.id === attendee.id) ||
        (item.objectID && attendee.objectID && item.objectID === attendee.objectID) ||
        (item.email && attendee.email && item.email === attendee.email && item.name === attendee.name)
      )
      if (index === -1) return null
      const validation = this.$v.listSelectedAttendees.$each[index]
      if (!validation || !validation.email) return null
      return validation.email.$dirty && validation.email.$error ? false : null
    },
    getMediaUrl(media) {
      if (media.thumbnails === undefined || media.thumbnails === null || typeof media.thumbnails === 'undefined') {
        return null
      }
      for (const thumbnail of media.thumbnails) {
        if (thumbnail.type === 1 || thumbnail.type === 'M') {
          return thumbnail.url
        }
      }

      return media.url
    }
  },
  validations() {
    const validationRules: ValidationsShape = {
      event: {
        title: { required },
        startDate: { required },
        endDate: {
          required,
          moreThanStarting: (value) => moreThanStarting(value, this)
        }
      },
      listSelectedAttendees: {
        $each: {
          email: {
            required,
            email
          }
        }
      }
    }
    if (this.event.recurringEvent) {
      validationRules.event.frequency = {
        required,
        isValidFrequency: (value) => {
          if (!value) return false
          return value !== 'EVENT_FREQUENCY_UNDEFINED' && ['EVENT_FREQUENCY_DAILY', 'EVENT_FREQUENCY_WEEKLY', 'EVENT_FREQUENCY_MONTHLY', 'EVENT_FREQUENCY_YEARLY'].includes(value)
        }
      }
    }
    if (this.event.frequency !== 'EVENT_FREQUENCY_UNDEFINED' && this.event.recurringEvent) {
      if (this.endingRecurrence === 'endByDateRecurrence' && this.event.recurrenceEndDate !== null) {
        validationRules.event.recurrenceEndDate = {
          required,
          moreThanRecurrenceStartDate: (value) => moreThanRecurrenceStartDate(value, this)
        }
      }
      validationRules.event.endDate = {
        dateOverlaps: (value) => dateOverlaps(value, this)
      }
      validationRules.event.recurrenceStartDate = { required }
      if (this.endingRecurrence === 'endAfter') {
        validationRules.event.occurrenceCount = { required, minValue: minValue(1), maxValue: maxValue(999) }
      }
    }
    return validationRules
  }
})
</script>
<style lang="scss" scoped>
:deep(.default-border) {
  border: 1px solid #ced4da;
}

:deep(.red-border) {
  border: 1px solid $color-red;
}

.title-name {
  line-height: 2;
}

.close-img {
  width: 15px;
}

:deep(table.table > thead > tr > th) {
  background-color: rgba(0, 68, 95, .06);
}

.uploaded-file {
  max-height: 100px;
  background-position: center center;
  background-size: cover;
  min-height: 5em;
}

.list-emails-select {
  max-width: 220px;
  margin-left: 1rem;
}

:deep(.attendees-table) {
  table-layout: auto;
  width: 100%;
}

:deep(.attendees-table td),
:deep(.attendees-table th) {
  white-space: normal;
  word-break: break-word;
}

:deep(.attendees-table .d-flex) {
  min-width: 0;
}

:deep(.attendees-table .text-break) {
  word-break: break-word;
  overflow-wrap: anywhere;
}

.img .media-block:first-child,
.video .media-block:first-child {
  padding-left: 0 !important;
}

.img .media-block:last-child,
.video .media-block:last-child {
  padding-right: 0 !important;
}

@media (min-width: 768px) {
  .uploaded-file {
    max-height: 100px;
    min-height: 3em;
  }
}

@media (min-width: 992px) {
  .uploaded-file {
    max-height: 110px;
    min-height: 3.5em;
  }
}

@media (min-width: 1200px) {
  .uploaded-file {
    max-height: 120px;
    min-height: 5em;
  }
}

@media (min-width: 1600px) {
  .uploaded-file {
    max-height: 130px;
    min-height: 6em;
  }
}

@media (min-width: 1900px) {
  .uploaded-file {
    max-height: 130px;
    min-height: 7em;
  }
}

@media (max-width: 767.98px) {
  :deep(.attendees-table th) {
    white-space: nowrap;
    text-align: center;
  }
}

.eventLocation :deep(.btn-outline) {
  width: 100%;
  color: #515151;
  background-color: #fff;
  border-color: #ced4da;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
  border-radius: 0.2rem;
}

.eventLocation.selected :deep(.btn-outline) {
  color: #212529;
  background-color: #e2e6ea;
  border-color: #dae0e5;
}

.description-text {
  word-wrap: break-word;
}

:deep(.show-data),
:deep(.show-data > span) {
  word-break: break-word;
  overflow-wrap: anywhere;
}

#modal-add-event___BV_modal_outer_ {
  z-index: 8001 !important;
}

#delete-confirmation-modal___BV_modal_outer_, #confirmationRecurrence___BV_modal_outer_ {
  z-index: 8001 !important;
}
:deep(.description-text p) {
  margin: 0
}
:deep(.max-width-70) {
  max-width: 70px;
}

#show-video-modal___BV_modal_outer_ {
  z-index: 8002 !important;
}
</style>
